// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/Library.js

import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAdvisor } from "../AdvisorContext";
import { Button, StatusChip } from "../design-system/components";
import ContextualExplainDrawer from "./ai/ContextualExplainDrawer";
import "./Library.css";

const MOCK_TEMPLATES = [
  {
    id: "tmpl-new-hire-onboarding",
    title: "New Hire Onboarding SOP",
    industry: "Any / Operations",
    area: "any",
    level: "Foundation",
    tags: ["HR", "Onboarding", "Access", "Training"],
    description:
      "Standardized onboarding flow for new employees, from paperwork and accounts to training and 30/60/90 check-ins.",
  },
  {
    id: "tmpl-gmp-batch-production",
    title: "GMP Batch Production Record",
    industry: "Supplements / CPG / GMP",
    area: "supplements",
    level: "Advanced",
    tags: ["GMP", "Production", "Batch Record"],
    description:
      "End-to-end batch record template for GMP production: materials, equipment, in-process checks, yields, and release.",
  },
  {
    id: "tmpl-cleaning-sanitation",
    title: "Cleaning & Sanitation SOP",
    industry: "Manufacturing / Facilities",
    area: "manufacturing",
    level: "Core",
    tags: ["Cleaning", "Sanitation", "GMP"],
    description:
      "Routine and deep-cleaning procedures for production areas, including frequencies, chemicals, and verification steps.",
  },
  {
    id: "tmpl-packaging-label-review",
    title: "Packaging & Label Review SOP",
    industry: "Supplements / CPG",
    area: "supplements",
    level: "Core",
    tags: ["Label Control", "Packaging", "Regulatory"],
    description:
      "How to review, approve, and release labels and packaging to stay aligned with brand, claims, and regulatory rules.",
  },
  {
    id: "tmpl-customer-support-escalation",
    title: "Customer Support Escalation SOP",
    industry: "Any / Support",
    area: "any",
    level: "Foundation",
    tags: ["Support", "Escalation", "CX"],
    description:
      "Step-by-step flow for handling tickets, triage, escalation paths, SLAs, and communication back to the customer.",
  },
  {
    id: "tmpl-capa",
    title: "CAPA (Corrective & Preventive Action) SOP",
    industry: "GMP / Quality",
    area: "quality_ops",
    level: "Advanced",
    tags: ["Quality", "CAPA", "Investigations"],
    description:
      "Structured CAPA process from deviation or complaint through root-cause analysis, corrective actions, and effectiveness checks.",
  },
  {
    id: "tmpl-complaint-handling",
    title: "Product Complaint Handling SOP",
    industry: "Supplements / CPG",
    area: "supplements",
    level: "Core",
    tags: ["Complaints", "Quality", "Safety"],
    description:
      "Standard procedure for logging, investigating, trending, and responding to product complaints and adverse events.",
  },
  {
    id: "tmpl-supplier-qualification",
    title: "Supplier Qualification & Onboarding SOP",
    industry: "Ops / Quality / Procurement",
    area: "quality_ops",
    level: "Core",
    tags: ["Suppliers", "Audits", "Qualifying"],
    description:
      "How to qualify, onboard, and periodically re-evaluate suppliers, including questionnaires, audits, and documentation.",
  },
  {
    id: "tmpl-equipment-calibration",
    title: "Equipment Calibration & Maintenance SOP",
    industry: "Manufacturing / Lab",
    area: "manufacturing",
    level: "Core",
    tags: ["Calibration", "Maintenance", "Equipment"],
    description:
      "Creating and maintaining a calibration and preventive maintenance schedule for critical equipment and instruments.",
  },
  {
    id: "tmpl-change-control",
    title: "Change Control SOP",
    industry: "Quality / Operations",
    area: "quality_ops",
    level: "Advanced",
    tags: ["Change Control", "Risk", "Approvals"],
    description:
      "Centralized process for proposing, reviewing, approving, and implementing changes to systems, documents, and equipment.",
  },
];

const TEMPLATE_AREA_LABELS = {
  any: "Any",
  manufacturing: "Manufacturing",
  supplements: "Supplements",
  quality_ops: "Quality & Ops",
};

const REQUEST_TIMEOUT_MS = 30000;
const SOP_EXPLAIN_PROMPTS = [
  "Is this SOP current?",
  "Why does this need review?",
  "What changed in this SOP?",
  "What is this SOP connected to?",
];

const SUPPORTED_UPLOAD_EXTENSIONS = [".pdf", ".docx"];
const MAX_UPLOAD_BYTES =
  Number(process.env.REACT_APP_SOP_UPLOAD_MAX_BYTES || 25 * 1024 * 1024) ||
  25 * 1024 * 1024;

function delay(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function formatDate(isoString) {
  if (!isoString) return "--";

  const raw = String(isoString).trim();

  /*
   * Controlled-document dates are compliance dates, not user-local moments.
   * Examples from the API:
   *   - effective_date: "2026-04-28"
   *   - due_at: "2026-05-03T00:00:00+00:00"
   *
   * If those are parsed directly with new Date(...), browsers west of UTC can
   * render the prior calendar day. For date-only / midnight-UTC values, preserve
   * the API calendar date exactly.
   */
  const dateOnlyMatch = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.\d+)?(?:Z|[+-]00:00)?)?$/
  );

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTime(isoString) {
  if (!isoString) return "--";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function hasRealUrl(url) {
  return Boolean(url && url !== "#" && String(url).trim() !== "");
}

function normalizeLibraryRow(row) {
  const latestMeta =
    row?.latest_version_metadata && typeof row.latest_version_metadata === "object"
      ? row.latest_version_metadata
      : {};
  const sourceType =
    latestMeta.source_type || latestMeta.type || row.type || "unknown";

  return {
    id: row.id,
    libraryCategory: "sop",
    title: row.title || "Untitled SOP",
    type: sourceType,
    createdAt:
      row.latest_version_created_at ||
      row.updated_at ||
      row.created_at ||
      null,
    status: row.latest_version_status || row.status || "unknown",
    pdfUrl: row.pdf_url || row.primary_document_url || "",
    pdfPreviewUrl: row.pdf_preview_url || "",
    docxUrl: row.docx_url || "",
    code: row.code || "",
    sopStatus: row.status || "",
    versionStatus: row.latest_version_status || "",
    isOutdated: Boolean(row.is_outdated),
    latestVersionId: row.latest_version_id || null,
    latestVersionNumber: row.latest_version_number,
    currentVersionId: row.current_version_id || row.latest_version_id || null,
    raw: row,
  };
}

function normalizeOverduePayload(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.sops)) return value.sops;
  if (Array.isArray(value?.overdue)) return value.overdue;
  if (Array.isArray(value?.outdated)) return value.outdated;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
}

function normalizePendingReviewPayload(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.sops)) return value.sops;
  if (Array.isArray(value?.pending_approval)) return value.pending_approval;
  if (Array.isArray(value?.pending_versions)) return value.pending_versions;
  if (Array.isArray(value?.versions)) return value.versions;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
}

function normalizePendingReviewRow(row, index = 0) {
  const latestMeta =
    row?.latest_version_metadata && typeof row.latest_version_metadata === "object"
      ? row.latest_version_metadata
      : row?.version?.metadata && typeof row.version.metadata === "object"
      ? row.version.metadata
      : {};

  const sop = row?.sop && typeof row.sop === "object" ? row.sop : {};
  const sopId = row?.sop_id || row?.sopId || sop.id || row?.id || "";
  const versionId =
    row?.version_id ||
    row?.latest_version_id ||
    row?.pending_version_id ||
    row?.version?.id ||
    row?.id ||
    "";

  const versionNumber =
    row?.version_number ||
    row?.latest_version_number ||
    row?.pending_version_number ||
    row?.version?.version_number ||
    null;

  return {
    id: sopId || versionId || `pending-review-${index}`,
    libraryCategory: "sop",
    title: row?.title || row?.sop_title || sop.title || "Untitled SOP",
    type:
      row?.source_type ||
      row?.type ||
      latestMeta.source_type ||
      latestMeta.type ||
      "uploaded_revision",
    createdAt:
      row?.created_at ||
      row?.latest_version_created_at ||
      row?.version?.created_at ||
      row?.updated_at ||
      null,
    status: "pending approval",
    pdfUrl: row?.pdf_url || row?.primary_document_url || row?.version?.pdf_url || "",
    pdfPreviewUrl: row?.pdf_preview_url || row?.version?.pdf_preview_url || "",
    docxUrl: row?.docx_url || row?.version?.docx_url || "",
    code: row?.code || row?.slug || sop.code || "",
    sopStatus: row?.sop_status || sop.status || row?.status || "",
    versionStatus: "pending approval",
    isOutdated: false,
    latestVersionId: versionId || null,
    latestVersionNumber: versionNumber,
    currentVersionId: row?.current_version_id || row?.currentVersionId || null,
    pendingVersionId: versionId || null,
    pendingVersionNumber: versionNumber,
    changeSummary:
      row?.change_summary ||
      row?.latest_version_change_summary ||
      row?.notes ||
      row?.version?.change_summary ||
      latestMeta.change_summary ||
      latestMeta.notes ||
      "",
    raw: {
      ...(row || {}),
      status: "pending approval",
      latest_version_status: "pending approval",
      latest_version_id: versionId || row?.latest_version_id || null,
      latest_version_number: versionNumber || row?.latest_version_number || null,
      pending_versions_count: 1,
    },
  };
}

function normalizePendingAcknowledgmentPayload(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.sops)) return value.sops;
  if (Array.isArray(value?.pending)) return value.pending;
  if (Array.isArray(value?.pending_acknowledgments)) return value.pending_acknowledgments;
  if (Array.isArray(value?.pending_attestations)) return value.pending_attestations;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
}

function normalizePendingAcknowledgmentRow(row) {
  const sopId = row?.sop_id || row?.sopId || row?.sop?.id || "";
  const requirementType =
    row?.requirement_type ||
    row?.attestation_type ||
    row?.type ||
    "read_and_understood";

  return {
    id: sopId,
    libraryCategory: "sop",
    title: row?.title || row?.sop_title || row?.sop?.title || "Untitled SOP",
    type: "acknowledgment_required",
    createdAt: row?.due_at || row?.effective_date || row?.created_at || null,
    status: "pending acknowledgment",
    pdfUrl: "",
    pdfPreviewUrl: "",
    docxUrl: "",
    code: row?.code || row?.slug || row?.sop?.code || "",
    sopStatus: "active",
    versionStatus: row?.release_status || "effective",
    isOutdated: false,
    latestVersionId: row?.version_id || row?.versionId || null,
    latestVersionNumber: row?.version_number || row?.version?.version_number || null,
    currentVersionId: row?.version_id || row?.versionId || null,
    requirementId: row?.requirement_id || row?.requirementId || row?.id || null,
    requirementType,
    dueAt: row?.due_at || row?.dueAt || null,
    effectiveDate: row?.effective_date || row?.effectiveDate || null,
    isAttestable: row?.is_attestable !== false,
    raw: {
      ...(row || {}),
      status: "pending acknowledgment",
      latest_version_status: row?.release_status || "effective",
    },
  };
}

function TypeBadge({ type }) {
  const normalized = (type || "unknown").toLowerCase();

  const label =
    normalized === "guided"
      ? "Guided"
      : normalized === "screenshot"
      ? "Screenshot"
      : normalized === "uploaded_revision"
      ? "Uploaded Revision"
      : normalized === "upload"
      ? "Uploaded"
      : normalized === "acknowledgment_required"
      ? "Acknowledgment"
      : "Unknown";

  const tone =
    normalized === "guided" || normalized === "screenshot"
      ? "informational"
      : normalized === "acknowledgment_required"
      ? "warning"
      : "neutral";

  return <StatusChip tone={tone}>{label}</StatusChip>;
}

function StatusBadge({ status, isOutdated }) {
  const normalized = (status || "").toLowerCase();
  let label = "Unknown";
  let tone = "neutral";

  if (normalized === "active") {
    label = isOutdated ? "Outdated" : "Active";
    tone = isOutdated ? "warning" : "success";
  } else if (normalized === "approved") {
    label = isOutdated ? "Outdated" : "Approved";
    tone = isOutdated ? "warning" : "success";
  } else if (normalized === "draft") {
    label = "Draft";
  } else if (normalized === "pending approval") {
    label = "Pending Approval";
    tone = "warning";
  } else if (normalized === "pending acknowledgment") {
    label = "Acknowledgment Required";
    tone = "warning";
  } else if (normalized === "superseded") {
    label = "Superseded";
  } else if (normalized === "rejected") {
    label = "Rejected";
    tone = "danger";
  } else if (normalized === "in_regeneration" || normalized === "under_revision") {
    label = "Processing";
    tone = "informational";
  } else if (normalized === "archived") {
    label = "Archived";
  } else if (["error", "failed", "automation_error"].includes(normalized)) {
    label = "Error";
    tone = "danger";
  }

  return <StatusChip tone={tone}>{label}</StatusChip>;
}

function normalizeStatusValue(status) {
  return String(status || "").trim().toLowerCase();
}

function getVersionLifecycleText(status, isCurrent) {
  const normalized = normalizeStatusValue(status);

  if (isCurrent && normalized === "active") {
    return "Current controlled version used for main preview, downloads, audits, and day-to-day execution.";
  }
  if (normalized === "pending approval") {
    return "Revision candidate awaiting approval. It is visible in history but is not the official controlled version yet.";
  }
  if (normalized === "superseded") {
    return "Historical version replaced by a newer approved revision. Retained for audit traceability.";
  }
  if (normalized === "rejected") {
    return "Rejected revision candidate. It remains visible for audit traceability but is not an official controlled version.";
  }
  if (normalized === "draft") {
    return "Draft version. It can be edited or submitted, but it is not official yet.";
  }
  if (normalized === "archived") {
    return "Archived historical version retained for controlled-document history.";
  }
  if (normalized === "approved") {
    return "Approved version. Review the current-version marker to confirm whether it is the active operational copy.";
  }

  return "Version retained in the controlled-document history.";
}

function getReleaseStatusLabel(status) {
  const normalized = String(status || "").trim().toLowerCase();

  if (normalized === "effective") return "Effective";
  if (normalized === "pending_effective_date") return "Pending Effective Date";
  if (normalized === "not_released") return "Not Released";
  if (normalized === "retired") return "Retired";
  return "Release Status Unknown";
}

function getReleaseStatusTone(status) {
  const normalized = String(status || "").trim().toLowerCase();

  if (normalized === "effective") return "active";
  if (normalized === "pending_effective_date") return "pending";
  if (normalized === "retired") return "historical";
  return "neutral";
}

function getAttestationRequirementSummary(requirements = [], attestations = [], userId = "") {
  const activeRequirements = Array.isArray(requirements)
    ? requirements.filter((item) => normalizeStatusValue(item?.status) === "active")
    : [];
  const userKey = String(userId || "").trim();
  const userAttestations = Array.isArray(attestations)
    ? attestations.filter((item) => {
        if (!userKey) return true;
        return String(item?.attested_by_user_id || item?.attested_by?.id || "").trim() === userKey;
      })
    : [];

  const userRequired = activeRequirements.filter((requirement) => {
    const requiredUser = String(requirement?.required_by_user_id || "").trim();
    return !requiredUser || !userKey || requiredUser === userKey;
  });

  const hasUserAttested = userAttestations.length > 0;
  const pendingUserRequirements = hasUserAttested ? [] : userRequired;

  return {
    activeRequirements,
    userRequired,
    userAttestations,
    hasUserAttested,
    pendingUserRequirements,
    totalActiveRequirements: activeRequirements.length,
    totalAttestations: Array.isArray(attestations) ? attestations.length : 0,
  };
}

function getRequirementLabel(requirement) {
  const type = String(requirement?.requirement_type || "acknowledgment").replace(/_/g, " ");
  return type.replace(/\b\w/g, (char) => char.toUpperCase());
}

function InlineControlPill({ children, tone = "neutral" }) {
  const tones = {
    active: {
      color: "#0C8F5D",
      background: "rgba(12, 143, 93, 0.08)",
      border: "1px solid rgba(12, 143, 93, 0.18)",
    },
    pending: {
      color: "#b45309",
      background: "rgba(180, 83, 9, 0.08)",
      border: "1px solid rgba(180, 83, 9, 0.18)",
    },
    historical: {
      color: "#64748b",
      background: "rgba(100, 116, 139, 0.08)",
      border: "1px solid rgba(100, 116, 139, 0.18)",
    },
    rejected: {
      color: "#b91c1c",
      background: "rgba(239, 68, 68, 0.07)",
      border: "1px solid rgba(239, 68, 68, 0.18)",
    },
    neutral: {
      color: "#64748b",
      background: "rgba(100, 116, 139, 0.08)",
      border: "1px solid rgba(100, 116, 139, 0.18)",
    },
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.24rem 0.5rem",
        borderRadius: "999px",
        fontSize: "0.72rem",
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        ...(tones[tone] || tones.neutral),
      }}
    >
      {children}
    </span>
  );
}

function SourceTypeBadge({ sourceType }) {
  const normalized = (sourceType || "").toLowerCase();

  if (!normalized) return null;

  let label = "Generated";
  let styles = {
    color: "#0C8F5D",
    background: "rgba(12, 143, 93, 0.08)",
    border: "1px solid rgba(12, 143, 93, 0.18)",
  };

  if (normalized === "uploaded_revision") {
    label = "Uploaded Revision";
    styles = {
      color: "#2563eb",
      background: "rgba(37, 99, 235, 0.08)",
      border: "1px solid rgba(37, 99, 235, 0.18)",
    };
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.24rem 0.5rem",
        borderRadius: "999px",
        fontSize: "0.72rem",
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        ...styles,
      }}
    >
      {label}
    </span>
  );
}

function DownloadAction({ url, label, icon, onClick }) {
  if (!hasRealUrl(url)) {
    return (
      <span
        className="library-link library-link--compact library-link--disabled"
        aria-disabled="true"
      >
        {icon} {label}
      </span>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="library-link library-link--compact"
      download
      onClick={onClick}
    >
      {icon} {label}
    </a>
  );
}

function pickLatestVersion(detail) {
  const versions = Array.isArray(detail?.versions) ? [...detail.versions] : [];
  if (!versions.length) return null;

  const currentVersionId = detail?.current_version_id || null;
  if (currentVersionId) {
    const canonical = versions.find((version) => version.id === currentVersionId);
    if (canonical) return canonical;
  }

  versions.sort((a, b) => {
    const aNum = Number(a.version_number || 0);
    const bNum = Number(b.version_number || 0);
    return bNum - aNum;
  });

  return versions[0];
}

function serializeComplianceRequirements(items) {
  if (!Array.isArray(items) || !items.length) return "";
  return items.join("\n");
}

function parseComplianceRequirements(text) {
  if (!text || !String(text).trim()) return [];
  return String(text)
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

function getVersionSourceType(versionMeta = {}) {
  return (
    versionMeta.source_type ||
    versionMeta.type ||
    versionMeta.input_mode ||
    versionMeta.builder_mode ||
    "generated"
  );
}

function getVersionFileType(version = null, versionMeta = {}) {
  const explicit = versionMeta.content_type || "";
  if (String(explicit).toLowerCase().includes("pdf")) return "PDF";
  if (String(explicit).toLowerCase().includes("word")) return "DOCX";

  const filename = (
    versionMeta.file_name ||
    versionMeta.source_filename ||
    version?.storage_key ||
    version?.primary_document_url ||
    ""
  ).toLowerCase();

  if (filename.endsWith(".pdf")) return "PDF";
  if (filename.endsWith(".docx")) return "DOCX";
  return null;
}

function getVersionPreviewStatus(version = null, versionMeta = {}) {
  if (versionMeta.preview_status) {
    return String(versionMeta.preview_status).toLowerCase();
  }

  if (version?.pdf_preview_url || versionMeta?.urls?.pdf_preview) {
    return "ready";
  }

  const fileType = getVersionFileType(version, versionMeta);
  if (fileType === "DOCX") return "processing";
  if (fileType === "PDF") return "ready";
  return "unknown";
}

function extractDetailView(detail, fallbackRow = null) {
  const latestVersion = pickLatestVersion(detail);

  const sopMeta =
    detail && detail.metadata && typeof detail.metadata === "object"
      ? detail.metadata
      : {};

  const versionMeta =
    latestVersion &&
    latestVersion.metadata &&
    typeof latestVersion.metadata === "object"
      ? latestVersion.metadata
      : {};

  const sections =
    sopMeta.sections && typeof sopMeta.sections === "object"
      ? sopMeta.sections
      : versionMeta.sections && typeof versionMeta.sections === "object"
      ? versionMeta.sections
      : {};

  const urls =
    versionMeta.urls && typeof versionMeta.urls === "object"
      ? versionMeta.urls
      : sopMeta.urls && typeof sopMeta.urls === "object"
      ? sopMeta.urls
      : {};

  const objective =
    sopMeta.display_objective ||
    sopMeta.objective ||
    sopMeta.original_objective ||
    versionMeta.objective ||
    sections.Objective ||
    sections.objective ||
    detail?.description ||
    "";

  const processName =
    sopMeta.display_process_name ||
    sopMeta.original_process_name ||
    sopMeta.process_name ||
    versionMeta.process_name ||
    detail?.title ||
    fallbackRow?.title ||
    "";

  const complianceRequirements = Array.isArray(
    sopMeta.display_compliance_requirements
  )
    ? sopMeta.display_compliance_requirements
    : Array.isArray(sopMeta.original_compliance_requirements)
    ? sopMeta.original_compliance_requirements
    : Array.isArray(sopMeta.compliance_requirements)
    ? sopMeta.compliance_requirements
    : Array.isArray(versionMeta.compliance_requirements)
    ? versionMeta.compliance_requirements
    : [];

  const latestVersionNotes =
    sopMeta.display_latest_version_notes ||
    sopMeta.latest_version_notes ||
    [latestVersion?.change_summary, latestVersion?.notes]
      .filter(Boolean)
      .join("\n\n") ||
    "";

  const pdfUrl =
    urls.pdf ||
    latestVersion?.pdf_url ||
    latestVersion?.primary_document_url ||
    fallbackRow?.pdfUrl ||
    "";

  const pdfPreviewUrl =
    urls.pdf_preview ||
    latestVersion?.pdf_preview_url ||
    fallbackRow?.pdfPreviewUrl ||
    "";

  const docxUrl =
    urls.docx || latestVersion?.docx_url || fallbackRow?.docxUrl || "";

  return {
    latestVersion,
    objective,
    processName,
    complianceRequirements,
    latestVersionNotes,
    pdfUrl,
    pdfPreviewUrl,
    docxUrl,
  };
}

function buildDetailFormState(detail, row, detailView) {
  return {
    title: detail?.title || row?.title || "",
    objective: detailView?.objective || "",
    processName: detailView?.processName || "",
    complianceRequirementsText: serializeComplianceRequirements(
      detailView?.complianceRequirements || []
    ),
    latestVersionNotes: detailView?.latestVersionNotes || "",
  };
}

function getUploadValidationMessage(file) {
  if (!file) {
    return "Choose a PDF or DOCX file to create a new controlled SOP version.";
  }

  const lowerName = String(file.name || "").toLowerCase();
  const supported = SUPPORTED_UPLOAD_EXTENSIONS.some((ext) =>
    lowerName.endsWith(ext)
  );

  if (!supported) {
    return "Only PDF and DOCX files are supported.";
  }

  if (file.size > MAX_UPLOAD_BYTES) {
    return "File exceeds the maximum upload size.";
  }

  return "";
}

function getEventLabel(eventType) {
  const normalized = String(eventType || "").trim().toLowerCase();

  if (normalized === "sop_generated") return "SOP generated";
  if (normalized === "version_uploaded") return "Revision uploaded";
  if (normalized === "submitted_for_approval") return "Submitted for approval";
  if (normalized === "version_approved") return "Version approved";
  if (normalized === "version_rejected") return "Version rejected";
  if (normalized === "version_superseded") return "Version superseded";
  if (normalized === "sop_archived") return "SOP archived";
  if (normalized === "sop_restored") return "SOP restored";
  if (normalized === "effective_date_set") return "Effective date set";
  if (normalized === "attestation_required") return "Attestation required";
  if (normalized === "sop_attested") return "SOP acknowledged";
  if (normalized === "sop_link_created") return "SOP-control link created";
  if (normalized === "sop_link_updated") return "SOP-control link updated";
  if (normalized === "sop_link_removed") return "SOP-control link removed";

  return normalized
    ? normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
    : "Document event";
}

function getEventTone(eventType) {
  const normalized = String(eventType || "").trim().toLowerCase();
  if (
    normalized === "version_approved" ||
    normalized === "sop_attested" ||
    normalized === "sop_link_created" ||
    normalized === "sop_link_updated"
  ) return "active";
  if (normalized === "version_rejected") return "rejected";
  if (
    normalized === "version_uploaded" ||
    normalized === "submitted_for_approval" ||
    normalized === "attestation_required" ||
    normalized === "effective_date_set"
  ) return "pending";
  if (
    normalized === "version_superseded" ||
    normalized === "sop_archived" ||
    normalized === "sop_link_removed"
  ) return "historical";
  return "neutral";
}

function getVersionNumberFromEvent(event) {
  const meta = event?.metadata && typeof event.metadata === "object" ? event.metadata : {};
  return meta.version_number || meta.superseded_by_version_number || null;
}

function actorLabel(actor) {
  if (!actor || typeof actor !== "object") return "Unknown reviewer";
  return actor.name || actor.email || actor.id || "Unknown reviewer";
}

function getApprovedByLabel(version) {
  const approvedBy =
    version?.approved_by && typeof version.approved_by === "object"
      ? version.approved_by
      : {};

  const name = String(approvedBy.name || "").trim();
  const email = String(approvedBy.email || "").trim();
  const id = String(approvedBy.id || version?.approved_by_user_id || "").trim();

  if (name && name.toLowerCase() !== "unknown reviewer" && name !== id) {
    return name;
  }

  if (email) return email;
  if (id) return id;
  return "";
}

function getReviewPermissions(detail) {
  const permissions =
    detail?.permissions && typeof detail.permissions === "object"
      ? detail.permissions
      : {};

  return {
    canApprove: permissions.can_approve !== false,
    canReject: permissions.can_reject !== false,
  };
}

function getPermissionMessage(canApprove, canReject) {
  if (canApprove || canReject) return "";
  return "You do not have SOP review authorization for this controlled document.";
}

function normalizeLibraryQueueFilter(value) {
  const normalized = String(value || "").trim().toLowerCase();

  if (
    normalized === "pending" ||
    normalized === "pending_approval" ||
    normalized === "pending approval" ||
    normalized === "pending_review" ||
    normalized === "drafts_in_review" ||
    normalized === "review"
  ) {
    return "pending_approval";
  }

  if (
    normalized === "acknowledgment" ||
    normalized === "acknowledgments" ||
    normalized === "pending_acknowledgment" ||
    normalized === "pending_acknowledgments" ||
    normalized === "attestation" ||
    normalized === "attestations" ||
    normalized === "pending_attestation" ||
    normalized === "pending_attestations"
  ) {
    return "pending_acknowledgment";
  }

  if (
    normalized === "overdue" ||
    normalized === "overdue_sops" ||
    normalized === "outdated"
  ) {
    return "overdue";
  }

  if (
    normalized === "archived" ||
    normalized === "archive" ||
    normalized === "archived_sops" ||
    normalized === "historical" ||
    normalized === "historical_sops"
  ) {
    return "archived";
  }

  if (
    normalized === "active_findings" ||
    normalized === "findings" ||
    normalized === "linked_findings"
  ) {
    return "active_findings";
  }

  return "all";
}

function isPendingApprovalRow(row) {
  const rowStatus = normalizeStatusValue(row?.status);
  const sopStatus = normalizeStatusValue(row?.sopStatus);
  const versionStatus = normalizeStatusValue(row?.versionStatus);
  const rawStatus = normalizeStatusValue(row?.raw?.status);
  const latestVersionStatus = normalizeStatusValue(row?.raw?.latest_version_status);
  const pendingVersionsCount = Number(row?.raw?.pending_versions_count || 0);

  return (
    rowStatus === "pending approval" ||
    rowStatus === "pending_approval" ||
    sopStatus === "pending approval" ||
    sopStatus === "pending_approval" ||
    versionStatus === "pending approval" ||
    versionStatus === "pending_approval" ||
    rawStatus === "pending approval" ||
    rawStatus === "pending_approval" ||
    latestVersionStatus === "pending approval" ||
    latestVersionStatus === "pending_approval" ||
    pendingVersionsCount > 0
  );
}

function isOverdueSopRow(row) {
  return Boolean(row?.isOutdated || row?.raw?.is_outdated || row?.raw?.is_overdue);
}

export default function Library({
  apiBaseUrl,
  getAccessToken,
  userProfile = null,
  initialStatusFilter = "all",
  sopQueueView = "all",
  routeParams = {},
  initialSopId = "",
  copilotTab = "",
  onDocumentControlChanged = null,
  standalone = false,
  themeMode = "dark",
  onModeChange = null,
}) {
  const { getAccessTokenSilently } = useAuth0();
  const { setCopilotContext, clearCopilotContext } = useAdvisor();
  const deletePopoverRef = useRef(null);
  const archivePopoverRef = useRef(null);
  const uploadInputRef = useRef(null);
  const autoOpenSopRef = useRef("");
  const documentControlRefreshTimerRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [sopView, setSopView] = useState("docs");
  const [templateArea, setTemplateArea] = useState("all");
  const [reloadTick, setReloadTick] = useState(0);
  const [actionState, setActionState] = useState({});
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [deleteConfirmRowId, setDeleteConfirmRowId] = useState(null);
  const [archiveConfirmRowId, setArchiveConfirmRowId] = useState(null);

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailTab, setDetailTab] = useState("details");
  const [previewKey, setPreviewKey] = useState(0);
  const [saveNotice, setSaveNotice] = useState("");

  const [sopExplainOpen, setSopExplainOpen] = useState(false);
  const [sopExplainQuestion, setSopExplainQuestion] = useState(SOP_EXPLAIN_PROMPTS[0]);
  const [sopExplainCard, setSopExplainCard] = useState(null);
  const [sopExplainLoading, setSopExplainLoading] = useState(false);
  const [sopExplainError, setSopExplainError] = useState("");

  const [detailForm, setDetailForm] = useState({
    title: "",
    objective: "",
    processName: "",
    complianceRequirementsText: "",
    latestVersionNotes: "",
  });
  const [detailFormBaseline, setDetailFormBaseline] = useState({
    title: "",
    objective: "",
    processName: "",
    complianceRequirementsText: "",
    latestVersionNotes: "",
  });

  const [uploadForm, setUploadForm] = useState({
    file: null,
    changeSummary: "",
    notes: "",
    effectiveDate: "",
  });
  const [uploadError, setUploadError] = useState("");
  const [uploadNotice, setUploadNotice] = useState("");
  const [uploadTouched, setUploadTouched] = useState(false);

  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");
  const [attestationRequirements, setAttestationRequirements] = useState([]);
  const [attestations, setAttestations] = useState([]);
  const [attestationLoading, setAttestationLoading] = useState(false);
  const [attestationError, setAttestationError] = useState("");
  const [attestationNotice, setAttestationNotice] = useState("");

  const [controlLinks, setControlLinks] = useState([]);
  const [controlLinksLoading, setControlLinksLoading] = useState(false);
  const [controlLinksError, setControlLinksError] = useState("");
  const [controlLinksNotice, setControlLinksNotice] = useState("");
  const [controlLinkForm, setControlLinkForm] = useState({
    controlId: "",
    ruleId: "",
    framework: "",
    priority: "100",
    notes: "",
  });
  const [pendingRemoveControlLinkId, setPendingRemoveControlLinkId] = useState(null);
  const [editingControlLinkId, setEditingControlLinkId] = useState(null);

  const [reviewDialog, setReviewDialog] = useState({
    mode: null,
    version: null,
    reason: "",
    notes: "",
    error: "",
  });

  const normalizedBase = useMemo(() => {
    const base = (apiBaseUrl || "").trim();
    if (base.endsWith("/")) return base.slice(0, -1);
    return base;
  }, [apiBaseUrl]);

  const tenantId =
    userProfile?.tenant_id ||
    userProfile?.tenantId ||
    process.env.REACT_APP_TENANT_ID ||
    "";

  const userId =
    userProfile?.user_id ||
    userProfile?.userId ||
    userProfile?.id ||
    userProfile?.sub ||
    "";

  const userDisplayName =
    userProfile?.name ||
    userProfile?.display_name ||
    userProfile?.displayName ||
    userProfile?.nickname ||
    "";

  const userEmail = userProfile?.email || userProfile?.user_email || "";

  const targetSopId = useMemo(() => {
    return String(
      initialSopId ||
        routeParams?.sopId ||
        routeParams?.sop_id ||
        routeParams?.id ||
        ""
    ).trim();
  }, [initialSopId, routeParams?.id, routeParams?.sopId, routeParams?.sop_id]);

  const activeQueueFilter = useMemo(() => {
    const queueFilter = normalizeLibraryQueueFilter(sopQueueView);
    if (queueFilter !== "all") return queueFilter;
    return normalizeLibraryQueueFilter(initialStatusFilter);
  }, [initialStatusFilter, sopQueueView]);

  const fetchToken = useCallback(async () => {
    if (typeof getAccessToken === "function") {
      return await getAccessToken();
    }

    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    return await getAccessTokenSilently(
      audience ? { authorizationParams: { audience } } : undefined
    );
  }, [getAccessToken, getAccessTokenSilently]);

  const getJsonWithToken = useCallback(
    async (token, path, params = {}, signal) => {
      const searchParams = new URLSearchParams();

      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.set(key, String(value));
        }
      });

      const url = searchParams.toString()
        ? `${normalizedBase}${path}?${searchParams.toString()}`
        : `${normalizedBase}${path}`;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;
      if (userDisplayName) headers["X-User-Name"] = userDisplayName;
      if (userEmail) headers["X-User-Email"] = userEmail;

      const res = await fetch(url, {
        method: "GET",
        headers,
        signal,
        cache: "no-store",
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        if (res.status === 401) {
          throw new Error("401 Unauthorized: Missing or invalid bearer token.");
        }
        if (res.status === 400 || res.status === 403) {
          throw new Error(
            `${res.status} ${res.statusText}: verify X-Tenant-Id / X-User-Id headers and tenant membership. ${text}`
          );
        }
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.json();
    },
    [normalizedBase, tenantId, userDisplayName, userEmail, userId]
  );

  const postJsonWithToken = useCallback(
    async (token, path, body = {}, signal) => {
      const url = `${normalizedBase}${path}`;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;
      if (userDisplayName) headers["X-User-Name"] = userDisplayName;
      if (userEmail) headers["X-User-Email"] = userEmail;

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body || {}),
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        if (res.status === 401) {
          throw new Error("401 Unauthorized: Missing or invalid bearer token.");
        }
        if (res.status === 400 || res.status === 403 || res.status === 409) {
          throw new Error(
            `${res.status} ${res.statusText}: ${
              text || "Request could not be completed."
            }`
          );
        }
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.json().catch(() => ({}));
    },
    [normalizedBase, tenantId, userDisplayName, userEmail, userId]
  );

  const postFormWithToken = useCallback(
    async (token, path, formData, signal) => {
      const url = `${normalizedBase}${path}`;

      const headers = {
        Authorization: `Bearer ${token}`,
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;
      if (userDisplayName) headers["X-User-Name"] = userDisplayName;
      if (userEmail) headers["X-User-Email"] = userEmail;

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: formData,
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        let friendly = text || "Upload failed. Your existing SOP versions were not changed.";

        if (friendly.includes("Only .pdf and .docx uploads are supported")) {
          friendly = "Only PDF and DOCX files are supported.";
        } else if (friendly.includes("exceeds the") || friendly.includes("too large")) {
          friendly = "File exceeds the maximum upload size.";
        }

        if (res.status === 401) {
          throw new Error("401 Unauthorized: Missing or invalid bearer token.");
        }

        throw new Error(friendly);
      }

      return res.json().catch(() => ({}));
    },
    [normalizedBase, tenantId, userDisplayName, userEmail, userId]
  );

  const patchJsonWithToken = useCallback(
    async (token, path, body = {}, signal) => {
      const url = `${normalizedBase}${path}`;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;
      if (userDisplayName) headers["X-User-Name"] = userDisplayName;
      if (userEmail) headers["X-User-Email"] = userEmail;

      const res = await fetch(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify(body || {}),
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        if (res.status === 401) {
          throw new Error("401 Unauthorized: Missing or invalid bearer token.");
        }
        if (res.status === 400 || res.status === 403 || res.status === 409) {
          throw new Error(
            `${res.status} ${res.statusText}: ${
              text || "Update could not be completed."
            }`
          );
        }
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.json().catch(() => ({}));
    },
    [normalizedBase, tenantId, userDisplayName, userEmail, userId]
  );

  const deleteJsonWithToken = useCallback(
    async (token, path, body = {}, signal) => {
      const url = `${normalizedBase}${path}`;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;
      if (userDisplayName) headers["X-User-Name"] = userDisplayName;
      if (userEmail) headers["X-User-Email"] = userEmail;

      const res = await fetch(url, {
        method: "DELETE",
        headers,
        body: JSON.stringify(body || {}),
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        if (res.status === 401) {
          throw new Error("401 Unauthorized: Missing or invalid bearer token.");
        }
        if (res.status === 400 || res.status === 403 || res.status === 409) {
          throw new Error(
            `${res.status} ${res.statusText}: ${
              text || "Delete could not be completed."
            }`
          );
        }
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.json().catch(() => ({}));
    },
    [normalizedBase, tenantId, userDisplayName, userEmail, userId]
  );

  const notifyDocumentControlChanged = useCallback(
    (event = {}) => {
      if (typeof onDocumentControlChanged === "function") {
        onDocumentControlChanged(event);
      }

      window.dispatchEvent(
        new CustomEvent("oasys:sop-document-control-changed", {
          detail: event,
        })
      );
    },
    [onDocumentControlChanged]
  );

  useEffect(() => {
    let mounted = true;

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort("sop-library-timeout"),
      REQUEST_TIMEOUT_MS
    );

    async function loadLibrary() {
      setLoading(true);
      setLoadError("");

      try {
        if (!normalizedBase) {
          throw new Error("SOP Library requires apiBaseUrl.");
        }

        if (!tenantId || !userId) {
          throw new Error(
            "SOP Library requires tenant and user identifiers. Verify userProfile contains tenant_id and a UUID user_id."
          );
        }

        const token = await fetchToken();
        const isPendingReviewQueue = activeQueueFilter === "pending_approval";
        const isOverdueQueue = activeQueueFilter === "overdue";
        const isArchivedQueue = activeQueueFilter === "archived";
        const isPendingAcknowledgmentQueue =
          activeQueueFilter === "pending_acknowledgment";

        const endpoint = isPendingAcknowledgmentQueue
          ? "/sops/attestations/pending"
          : isPendingReviewQueue
          ? "/sops/pending-approval"
          : isOverdueQueue
          ? "/sops/overdue"
          : "/sops/library";

        const params = isPendingAcknowledgmentQueue
          ? { limit: 250, tenant_id: tenantId }
          : isPendingReviewQueue
          ? { limit: 250, tenant_id: tenantId }
          : isOverdueQueue
          ? { refresh: true, limit: 250 }
          : isArchivedQueue
          ? { is_archived: true, limit: 250, tenant_id: tenantId }
          : { is_archived: false, limit: 250, tenant_id: tenantId };

        const data = await getJsonWithToken(
          token,
          endpoint,
          params,
          controller.signal
        );

        if (!mounted) return;

        const sourceRows = isPendingAcknowledgmentQueue
          ? normalizePendingAcknowledgmentPayload(data)
          : isPendingReviewQueue
          ? normalizePendingReviewPayload(data)
          : isOverdueQueue
          ? normalizeOverduePayload(data)
          : data;

        const normalizedRows = Array.isArray(sourceRows)
          ? sourceRows.map((row, index) =>
              isPendingAcknowledgmentQueue
                ? normalizePendingAcknowledgmentRow(row)
                : isPendingReviewQueue
                ? normalizePendingReviewRow(row, index)
                : normalizeLibraryRow(row)
            )
          : [];

        setRows(normalizedRows);
      } catch (error) {
        if (!mounted) return;
        console.error("Failed to load SOP library", error);
        setRows([]);
        setLoadError(
          error?.message || "Unable to load the SOP library right now."
        );
      } finally {
        clearTimeout(timeoutId);
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadLibrary();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [
    activeQueueFilter,
    fetchToken,
    getJsonWithToken,
    normalizedBase,
    tenantId,
    userId,
    reloadTick,
  ]);

  useEffect(() => {
    if (!selectedRowId) return;

    const stillExists = rows.some((row) => row.id === selectedRowId);
    if (!stillExists) {
      setSopExplainOpen(false);
      setSopExplainCard(null);
      setSopExplainError("");
      setSelectedRowId(null);
      setSelectedDetail(null);
      setDetailError("");
      setDetailLoading(false);
      setDetailTab("details");
      setPreviewKey(0);
      setUploadForm({
        file: null,
        changeSummary: "",
        notes: "",
        effectiveDate: "",
      });
      setUploadError("");
      setUploadNotice("");
      setUploadTouched(false);
      setAuditEvents([]);
      setAuditError("");
      setAuditLoading(false);
      setAttestationRequirements([]);
      setAttestations([]);
      setAttestationError("");
      setAttestationLoading(false);
      setAttestationNotice("");
      setControlLinks([]);
      setControlLinksError("");
      setControlLinksLoading(false);
      setControlLinksNotice("");
      setControlLinkForm({
        controlId: "",
        ruleId: "",
        framework: "",
        priority: "100",
        notes: "",
      });
      setPendingRemoveControlLinkId(null);
      setEditingControlLinkId(null);
      setReviewDialog({ mode: null, version: null, reason: "", notes: "", error: "" });
    }
  }, [rows, selectedRowId]);

  useEffect(() => {
    if (!saveNotice) return undefined;
    const timeoutId = window.setTimeout(() => {
      setSaveNotice("");
    }, 2500);

    return () => window.clearTimeout(timeoutId);
  }, [saveNotice]);

  useEffect(() => {
    if (!uploadNotice) return undefined;
    const timeoutId = window.setTimeout(() => {
      setUploadNotice("");
    }, 4000);

    return () => window.clearTimeout(timeoutId);
  }, [uploadNotice]);

  useEffect(() => {
    if (!attestationNotice) return undefined;
    const timeoutId = window.setTimeout(() => {
      setAttestationNotice("");
    }, 4000);

    return () => window.clearTimeout(timeoutId);
  }, [attestationNotice]);

  useEffect(() => {
    if (!controlLinksNotice) return undefined;
    const timeoutId = window.setTimeout(() => {
      setControlLinksNotice("");
    }, 4000);

    return () => window.clearTimeout(timeoutId);
  }, [controlLinksNotice]);

  useEffect(() => {
    if (!deleteConfirmRowId && !archiveConfirmRowId) return undefined;

    const handlePointerDown = (event) => {
      if (
        deleteConfirmRowId &&
        deletePopoverRef.current &&
        !deletePopoverRef.current.contains(event.target)
      ) {
        setDeleteConfirmRowId(null);
      }

      if (
        archiveConfirmRowId &&
        archivePopoverRef.current &&
        !archivePopoverRef.current.contains(event.target)
      ) {
        setArchiveConfirmRowId(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setDeleteConfirmRowId(null);
        setArchiveConfirmRowId(null);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [archiveConfirmRowId, deleteConfirmRowId]);

  const handleManualRefresh = useCallback(() => {
    setLoadError("");
    setReloadTick((value) => value + 1);
  }, []);

  useEffect(() => {
    const refreshEventTypes = new Set([
      "sop_generated",
      "sop_generation_complete",
      "sop_created",
      "sop_updated",
      "sop_submit",
      "sop_archive",
      "sop_restore",
      "sop_restored",
      "sop_rename",
      "sop_deleted",
      "version_uploaded",
      "version_approved",
      "version_rejected",
      "version_superseded",
      "submitted_for_approval",
      "effective_date_set",
      "attestation_required",
      "attestation_completed",
      "sop_attested",
      "sop_link_created",
      "sop_link_updated",
      "sop_link_removed",
    ]);

    const shouldRefreshForEvent = (eventType) => {
      if (!eventType) return true;
      if (refreshEventTypes.has(eventType)) return true;
      if (eventType.startsWith("sop_")) return true;
      if (eventType.startsWith("version_")) return true;
      if (eventType.includes("attestation")) return true;
      if (eventType.includes("document_control")) return true;
      return false;
    };

    const handleDocumentControlChanged = (event) => {
      const eventType = String(event?.detail?.type || "").trim().toLowerCase();

      if (!shouldRefreshForEvent(eventType)) {
        return;
      }

      if (documentControlRefreshTimerRef.current) {
        window.clearTimeout(documentControlRefreshTimerRef.current);
      }

      documentControlRefreshTimerRef.current = window.setTimeout(() => {
        setLoadError("");
        setReloadTick((value) => value + 1);
      }, 300);
    };

    window.addEventListener(
      "oasys:sop-document-control-changed",
      handleDocumentControlChanged
    );

    return () => {
      if (documentControlRefreshTimerRef.current) {
        window.clearTimeout(documentControlRefreshTimerRef.current);
        documentControlRefreshTimerRef.current = null;
      }

      window.removeEventListener(
        "oasys:sop-document-control-changed",
        handleDocumentControlChanged
      );
    };
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let filteredRows = [...rows];

    if (activeQueueFilter !== "archived") {
      filteredRows = filteredRows.filter((row) => {
        const rowStatus = normalizeStatusValue(row?.status);
        const rawStatus = normalizeStatusValue(row?.raw?.status);
        return (
          rowStatus !== "archived" &&
          rawStatus !== "archived" &&
          !Boolean(row?.raw?.is_archived)
        );
      });
    }

    if (activeQueueFilter === "pending_approval") {
      filteredRows = filteredRows.filter(isPendingApprovalRow);
    } else if (activeQueueFilter === "pending_acknowledgment") {
      filteredRows = filteredRows.filter((row) => row?.status === "pending acknowledgment");
    } else if (activeQueueFilter === "overdue") {
      filteredRows = filteredRows.filter(isOverdueSopRow);
    } else if (activeQueueFilter === "archived") {
      filteredRows = filteredRows.filter((row) => {
        const rowStatus = normalizeStatusValue(row?.status);
        const rawStatus = normalizeStatusValue(row?.raw?.status);
        return (
          rowStatus === "archived" ||
          rawStatus === "archived" ||
          Boolean(row?.raw?.is_archived)
        );
      });
    } else if (activeQueueFilter === "active_findings") {
      filteredRows = filteredRows.filter((row) =>
        Boolean(row?.raw?.active_findings_count || row?.raw?.linked_findings_count)
      );
    }

    if (term) {
      filteredRows = filteredRows.filter((row) => {
        const title = row.title?.toLowerCase() || "";
        const code = row.code?.toLowerCase() || "";
        return title.includes(term) || code.includes(term);
      });
    }

    if (categoryFilter !== "all") {
      filteredRows = filteredRows.filter(
        (row) => row.libraryCategory === categoryFilter
      );
    }

    if (sortOrder === "newest") {
      filteredRows.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } else if (sortOrder === "oldest") {
      filteredRows.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    } else if (sortOrder === "az") {
      filteredRows.sort((a, b) =>
        a.title.localeCompare(b.title, undefined, { sensitivity: "base" })
      );
    }

    return filteredRows;
  }, [rows, search, categoryFilter, sortOrder, activeQueueFilter]);

  const isTemplateView = categoryFilter === "sop" && sopView === "templates";

  const templateAreas = useMemo(() => {
    const set = new Set();
    MOCK_TEMPLATES.forEach((t) => {
      if (t.area) set.add(t.area);
    });
    return Array.from(set);
  }, []);

  const filteredTemplates = useMemo(() => {
    let items = [...MOCK_TEMPLATES];
    const term = search.trim().toLowerCase();

    if (term) {
      items = items.filter(
        (tpl) =>
          tpl.title.toLowerCase().includes(term) ||
          tpl.description.toLowerCase().includes(term)
      );
    }

    if (templateArea !== "all") {
      items = items.filter((tpl) => tpl.area === templateArea);
    }

    if (sortOrder === "az") {
      items.sort((a, b) =>
        a.title.localeCompare(b.title, undefined, { sensitivity: "base" })
      );
    }

    return items;
  }, [search, templateArea, sortOrder]);

  const selectedRow =
    filtered.find((row) => row.id === selectedRowId) ||
    rows.find((row) => row.id === selectedRowId) ||
    null;

  const detailView = useMemo(() => {
    if (!selectedDetail && !selectedRow) return null;
    return extractDetailView(selectedDetail, selectedRow);
  }, [selectedDetail, selectedRow]);

  useEffect(() => {
    const sopId = String(selectedRowId || "").trim();
    const pageContext = {
      page: standalone ? "library" : "sop_center",
      surface: standalone ? "sop_library" : "sop_center",
      intelligence_mode: sopId ? "contextual_explain" : "page",
      section:
        sopQueueView && sopQueueView !== "all"
          ? sopQueueView
          : "library",
      tab: sopId ? detailTab : copilotTab || sopView,
      ...(initialStatusFilter && initialStatusFilter !== "all"
        ? { filter: initialStatusFilter }
        : {}),
    };

    if (!sopId) {
      clearCopilotContext();
      setCopilotContext(pageContext);
      return;
    }

    setCopilotContext({
      ...pageContext,
      record_type: "sop",
      sop_id: sopId,
      focus_label: String(
        selectedDetail?.title || selectedRow?.title || "Selected SOP"
      ),
      focus_type: "SOP",
    });
  }, [
    clearCopilotContext,
    copilotTab,
    detailTab,
    initialStatusFilter,
    selectedDetail?.title,
    selectedRow?.title,
    selectedRowId,
    setCopilotContext,
    sopQueueView,
    sopView,
    standalone,
  ]);


  const askSopExplain = useCallback(
    async (question) => {
      if (!selectedRowId || !selectedDetail) return;

      const nextQuestion = String(question || SOP_EXPLAIN_PROMPTS[0]).trim();
      setSopExplainQuestion(nextQuestion);
      setSopExplainLoading(true);
      setSopExplainError("");
      setSopExplainCard(null);

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort("sop-intelligence-timeout"),
        REQUEST_TIMEOUT_MS
      );

      try {
        const token = await fetchToken();
        const response = await postJsonWithToken(
          token,
          "/ai/copilot/ask",
          {
            scope: "sop",
            question: nextQuestion,
            copilot_context: {
              page: "sop_center",
              surface: "sop_page",
              intelligence_mode: "contextual_explain",
              record_type: "sop",
              sop_id: selectedRowId,
              status: selectedDetail?.status || selectedRow?.status || null,
              current_version_id:
                selectedDetail?.current_version_id ||
                selectedRow?.currentVersionId ||
                null,
              code: selectedDetail?.code || selectedRow?.code || null,
            },
          },
          controller.signal
        );

        const card =
          response?.card && typeof response.card === "object"
            ? response.card
            : null;

        if (!card) {
          throw new Error("SOP explanation did not return an intelligence card.");
        }

        setSopExplainCard(card);
      } catch (error) {
        console.error("Contextual SOP Explain failed", error);
        setSopExplainError(
          error?.name === "AbortError" || String(error?.message || "").includes("sop-intelligence-timeout")
            ? "Explanation unavailable: sop-intelligence-timeout"
            : error?.message || "Unable to explain this SOP right now."
        );
      } finally {
        window.clearTimeout(timeoutId);
        setSopExplainLoading(false);
      }
    },
    [
      fetchToken,
      postJsonWithToken,
      selectedDetail,
      selectedRow,
      selectedRowId,
    ]
  );

  const openSopExplain = useCallback(() => {
    if (!selectedRowId || !selectedDetail) return;
    setSopExplainOpen(true);
    setSopExplainQuestion(SOP_EXPLAIN_PROMPTS[0]);
    setSopExplainCard(null);
    setSopExplainError("");
    askSopExplain(SOP_EXPLAIN_PROMPTS[0]);
  }, [askSopExplain, selectedDetail, selectedRowId]);

  const closeSopExplain = useCallback(() => {
    setSopExplainOpen(false);
    setSopExplainLoading(false);
    setSopExplainError("");
  }, []);

  const selectedNormalizedStatus = (
    selectedRow?.status ||
    selectedDetail?.status ||
    ""
  ).toLowerCase();

  const selectedCanSubmit = selectedNormalizedStatus === "draft";
  const selectedCanArchive = [
    "draft",
    "pending approval",
    "approved",
    "active",
  ].includes(selectedNormalizedStatus);
  const selectedCanEdit = [
    "draft",
    "pending approval",
    "approved",
    "active",
  ].includes(selectedNormalizedStatus);
  const selectedReviewPermissions = useMemo(
    () => getReviewPermissions(selectedDetail),
    [selectedDetail]
  );
  const selectedCanApproveVersion = selectedReviewPermissions.canApprove;
  const selectedCanRejectVersion = selectedReviewPermissions.canReject;
  const selectedReviewPermissionMessage = getPermissionMessage(
    selectedCanApproveVersion,
    selectedCanRejectVersion
  );

  const currentReleaseStatus = selectedDetail?.current_release_status || null;
  const currentEffectiveDate = selectedDetail?.current_effective_date || null;
  const currentIsAttestable = Boolean(selectedDetail?.current_is_attestable);
  const currentVersionForAttestation = useMemo(() => {
    const currentId = selectedDetail?.current_version_id || null;
    if (!currentId || !Array.isArray(selectedDetail?.versions)) return detailView?.latestVersion || null;
    return selectedDetail.versions.find((version) => version.id === currentId) || detailView?.latestVersion || null;
  }, [detailView, selectedDetail]);

  const attestationSummary = useMemo(
    () => getAttestationRequirementSummary(attestationRequirements, attestations, userId),
    [attestationRequirements, attestations, userId]
  );

  const isDetailDirty = useMemo(() => {
    return (
      detailForm.title !== detailFormBaseline.title ||
      detailForm.objective !== detailFormBaseline.objective ||
      detailForm.processName !== detailFormBaseline.processName ||
      detailForm.complianceRequirementsText !==
        detailFormBaseline.complianceRequirementsText ||
      detailForm.latestVersionNotes !== detailFormBaseline.latestVersionNotes
    );
  }, [detailForm, detailFormBaseline]);

  const versionHistory = useMemo(() => {
    const versions = Array.isArray(selectedDetail?.versions)
      ? [...selectedDetail.versions]
      : [];

    const currentVersionId = selectedDetail?.current_version_id || null;

    versions.sort((a, b) => {
      if (currentVersionId) {
        if (a.id === currentVersionId && b.id !== currentVersionId) return -1;
        if (b.id === currentVersionId && a.id !== currentVersionId) return 1;
      }

      const aNum = Number(a.version_number || 0);
      const bNum = Number(b.version_number || 0);
      return bNum - aNum;
    });

    return versions;
  }, [selectedDetail]);

  const confirmDiscardUnsavedChanges = useCallback(() => {
    return window.confirm(
      "You have unsaved changes. Close this SOP and discard those edits?"
    );
  }, []);

  const refreshSelectedDetail = useCallback(
    async (row, options = {}) => {
      const { preserveTab = true, nextTab = null, bumpPreview = false } = options;
      if (!row?.id) return null;

      const token = await fetchToken();
      const detail = await getJsonWithToken(token, `/sops/${row.id}`, {}, undefined);
      const nextDetailView = extractDetailView(detail, row);
      const nextForm = buildDetailFormState(detail, row, nextDetailView);

      setSelectedDetail(detail);
      setDetailForm(nextForm);
      setDetailFormBaseline(nextForm);
      setAuditLoading(true);
      setAuditError("");
      setAttestationLoading(true);
      setAttestationError("");

      try {
        const events = await getJsonWithToken(token, `/sops/${row.id}/events`, {}, undefined);
        setAuditEvents(Array.isArray(events) ? events : []);
      } catch (eventError) {
        console.error("Failed to load SOP audit events", eventError);
        setAuditEvents([]);
        setAuditError(eventError?.message || "Unable to load document-control timeline.");
      } finally {
        setAuditLoading(false);
      }

      try {
        const currentVersionId = detail?.current_version_id || nextDetailView?.latestVersion?.id || "";
        const requirements = await getJsonWithToken(
          token,
          `/sops/${row.id}/attestation-requirements`,
          currentVersionId ? { version_id: currentVersionId } : {},
          undefined
        );

        setAttestationRequirements(Array.isArray(requirements) ? requirements : []);

        if (currentVersionId) {
          const attestationRows = await getJsonWithToken(
            token,
            `/sops/${row.id}/versions/${currentVersionId}/attestations`,
            {},
            undefined
          );
          setAttestations(Array.isArray(attestationRows) ? attestationRows : []);
        } else {
          setAttestations([]);
        }
      } catch (attestationLoadError) {
        console.error("Failed to load SOP attestations", attestationLoadError);
        setAttestationRequirements([]);
        setAttestations([]);
        setAttestationError(
          attestationLoadError?.message || "Unable to load SOP acknowledgment status."
        );
      } finally {
        setAttestationLoading(false);
      }

      setControlLinksLoading(true);
      setControlLinksError("");
      try {
        const links = await getJsonWithToken(
          token,
          `/sops/${row.id}/links`,
          {},
          undefined
        );
        setControlLinks(Array.isArray(links) ? links : []);
      } catch (controlLinkLoadError) {
        console.error("Failed to load SOP control links", controlLinkLoadError);
        setControlLinks([]);
        setControlLinksError(
          controlLinkLoadError?.message || "Unable to load linked controls."
        );
      } finally {
        setControlLinksLoading(false);
      }

      if (nextTab) {
        setDetailTab(nextTab);
      } else if (!preserveTab) {
        setDetailTab("details");
      }

      if (bumpPreview) {
        setPreviewKey((value) => value + 1);
      }

      return detail;
    },
    [fetchToken, getJsonWithToken]
  );

  const forceRefreshSelectedDetail = useCallback(
    async (row, options = {}) => {
      if (!row?.id) return null;

      const firstRefresh = await refreshSelectedDetail(row, options);

      await delay(350);

      const secondRefresh = await refreshSelectedDetail(row, {
        ...options,
        preserveTab: options.preserveTab ?? true,
      });

      return secondRefresh || firstRefresh;
    },
    [refreshSelectedDetail]
  );

  const fetchDetail = useCallback(
    async (row) => {
      if (!row?.id) return;

      setSelectedRowId(row.id);
      setDetailLoading(true);
      setDetailError("");
      setSaveNotice("");
      setUploadError("");
      setUploadNotice("");
      setUploadTouched(false);
      setAuditEvents([]);
      setAuditError("");
      setAuditLoading(false);
      setAttestationRequirements([]);
      setAttestations([]);
      setAttestationError("");
      setAttestationLoading(false);
      setAttestationNotice("");
      setControlLinks([]);
      setControlLinksError("");
      setControlLinksLoading(false);
      setControlLinksNotice("");
      setControlLinkForm({
        controlId: "",
        ruleId: "",
        framework: "",
        priority: "100",
        notes: "",
      });
      setPendingRemoveControlLinkId(null);
      setEditingControlLinkId(null);
      setReviewDialog({ mode: null, version: null, reason: "", notes: "", error: "" });
      setDetailTab("details");
      setPreviewKey((value) => value + 1);

      try {
        await refreshSelectedDetail(row, { preserveTab: false });
      } catch (error) {
        console.error("Failed to load SOP detail", error);
        setSelectedDetail(null);
        setDetailError(
          error?.message || "Unable to load SOP detail right now."
        );
      } finally {
        setDetailLoading(false);
      }
    },
    [refreshSelectedDetail]
  );

  const closeDetailModal = useCallback(
    (force = false) => {
      if (!force && selectedCanEdit && isDetailDirty) {
        const discard = confirmDiscardUnsavedChanges();
        if (!discard) return;
      }

      setSelectedRowId(null);
      setSelectedDetail(null);
      setDetailError("");
      setDetailLoading(false);
      setDetailTab("details");
      setPreviewKey(0);
      setSaveNotice("");
      setUploadError("");
      setUploadNotice("");
      setUploadTouched(false);
      setAuditEvents([]);
      setAuditError("");
      setAuditLoading(false);
      setAttestationRequirements([]);
      setAttestations([]);
      setAttestationError("");
      setAttestationLoading(false);
      setAttestationNotice("");
      setControlLinks([]);
      setControlLinksError("");
      setControlLinksLoading(false);
      setControlLinksNotice("");
      setControlLinkForm({
        controlId: "",
        ruleId: "",
        framework: "",
        priority: "100",
        notes: "",
      });
      setPendingRemoveControlLinkId(null);
      setEditingControlLinkId(null);
      setReviewDialog({ mode: null, version: null, reason: "", notes: "", error: "" });
      setArchiveConfirmRowId(null);
      setDeleteConfirmRowId(null);
      setUploadForm({
        file: null,
        changeSummary: "",
        notes: "",
        effectiveDate: "",
      });
      if (uploadInputRef.current) {
        uploadInputRef.current.value = "";
      }
      setDetailForm({
        title: "",
        objective: "",
        processName: "",
        complianceRequirementsText: "",
        latestVersionNotes: "",
      });
      setDetailFormBaseline({
        title: "",
        objective: "",
        processName: "",
        complianceRequirementsText: "",
        latestVersionNotes: "",
      });
    },
    [confirmDiscardUnsavedChanges, isDetailDirty, selectedCanEdit]
  );

  const handleRowSelect = useCallback(
    (row) => {
      if (!row?.id) return;
      if (editingRowId === row.id) return;

      if (
        selectedRowId &&
        selectedRowId !== row.id &&
        selectedCanEdit &&
        isDetailDirty
      ) {
        const discard = confirmDiscardUnsavedChanges();
        if (!discard) return;
      }

      fetchDetail(row);
    },
    [
      confirmDiscardUnsavedChanges,
      editingRowId,
      fetchDetail,
      isDetailDirty,
      selectedCanEdit,
      selectedRowId,
    ]
  );

  useEffect(() => {
    if (!targetSopId || loading || detailLoading) return;
    if (selectedRowId === targetSopId) return;
    if (autoOpenSopRef.current === targetSopId) return;

    const targetRow =
      rows.find((row) => String(row?.id || "") === targetSopId) ||
      rows.find((row) => String(row?.raw?.id || "") === targetSopId) ||
      rows.find((row) => String(row?.sop_id || row?.sopId || "") === targetSopId);

    if (!targetRow) return;

    autoOpenSopRef.current = targetSopId;
    setSearch("");
    setCategoryFilter("all");
    setSopView("docs");
    setTemplateArea("all");
    fetchDetail(targetRow);
  }, [
    detailLoading,
    fetchDetail,
    loading,
    rows,
    selectedRowId,
    targetSopId,
  ]);

  const stopRowClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const performLifecycleAction = useCallback(
    async (row, action) => {
      const rowId = row.id;
      const actionKey = `${rowId}:${action}`;

      setActionState((prev) => ({ ...prev, [actionKey]: true }));
      setLoadError("");
      setDetailError("");
      setSaveNotice("");
      setUploadError("");
      setUploadNotice("");
      if (action === "archive") {
        setArchiveConfirmRowId(null);
      }

      try {
        const token = await fetchToken();
        const endpoint =
          action === "submit"
            ? `/sops/${rowId}/submit-for-approval`
            : `/sops/${rowId}/archive`;

        await postJsonWithToken(token, endpoint, {}, undefined);

        setDeleteConfirmRowId(null);
        setArchiveConfirmRowId(null);
        setReloadTick((value) => value + 1);

        if (selectedRowId === rowId) {
          await refreshSelectedDetail(row, {
            preserveTab: true,
            bumpPreview: action === "submit",
          });
        }

        notifyDocumentControlChanged({
          type: `sop_${action}`,
          sopId: rowId,
          action,
        });
      } catch (error) {
        console.error(`Failed to ${action} SOP`, error);
        const message =
          error?.message ||
          `Unable to ${
            action === "submit"
              ? "submit this SOP for approval"
              : "archive this SOP"
          } right now.`;
        setLoadError(message);
        setDetailError(message);
      } finally {
        setActionState((prev) => ({ ...prev, [actionKey]: false }));
      }
    },
    [
      fetchToken,
      notifyDocumentControlChanged,
      postJsonWithToken,
      refreshSelectedDetail,
      selectedRowId,
    ]
  );

  const beginRename = useCallback((row) => {
    setEditingRowId(row.id);
    setEditingTitle(row.title || "");
    setDeleteConfirmRowId(null);
    setArchiveConfirmRowId(null);
    setLoadError("");
    setDetailError("");
  }, []);

  const cancelRename = useCallback(() => {
    setEditingRowId(null);
    setEditingTitle("");
  }, []);

  const saveRename = useCallback(
    async (row) => {
      const rowId = row.id;
      const actionKey = `${rowId}:rename`;
      const nextTitle = (editingTitle || "").trim();

      if (!nextTitle) {
        setLoadError("Title cannot be empty.");
        return;
      }

      if (nextTitle === (row.title || "").trim()) {
        setEditingRowId(null);
        setEditingTitle("");
        return;
      }

      setActionState((prev) => ({ ...prev, [actionKey]: true }));
      setLoadError("");
      setDetailError("");

      try {
        const token = await fetchToken();
        await patchJsonWithToken(
          token,
          `/sops/${rowId}`,
          { title: nextTitle },
          undefined
        );

        setEditingRowId(null);
        setEditingTitle("");
        setReloadTick((value) => value + 1);

        if (selectedRowId === rowId) {
          await refreshSelectedDetail(row, { preserveTab: true });
        }
      } catch (error) {
        console.error("Failed to rename SOP", error);
        const message =
          error?.message || "Unable to rename this SOP right now.";
        setLoadError(message);
        setDetailError(message);
      } finally {
        setActionState((prev) => ({ ...prev, [actionKey]: false }));
      }
    },
    [editingTitle, fetchToken, patchJsonWithToken, refreshSelectedDetail, selectedRowId]
  );

  const confirmArchive = useCallback((rowId) => {
    setArchiveConfirmRowId((current) => (current === rowId ? null : rowId));
    setDeleteConfirmRowId(null);
    setEditingRowId(null);
    setEditingTitle("");
    setLoadError("");
    setDetailError("");
  }, []);

  const cancelArchive = useCallback(() => {
    setArchiveConfirmRowId(null);
  }, []);

  const confirmDelete = useCallback((rowId) => {
    setDeleteConfirmRowId((current) => (current === rowId ? null : rowId));
    setArchiveConfirmRowId(null);
    setEditingRowId(null);
    setEditingTitle("");
    setLoadError("");
    setDetailError("");
  }, []);

  const cancelDelete = useCallback(() => {
    setDeleteConfirmRowId(null);
  }, []);

  const performDelete = useCallback(
    async (row) => {
      const rowId = row.id;
      const actionKey = `${rowId}:delete`;

      setActionState((prev) => ({ ...prev, [actionKey]: true }));
      setLoadError("");
      setDetailError("");

      try {
        const token = await fetchToken();
        await deleteJsonWithToken(
          token,
          `/sops/${rowId}`,
          { confirm: true },
          undefined
        );

        setDeleteConfirmRowId(null);

        if (selectedRowId === rowId) {
          closeDetailModal(true);
        }

        setReloadTick((value) => value + 1);
      } catch (error) {
        console.error("Failed to delete SOP", error);
        const message =
          error?.message || "Unable to delete this SOP right now.";
        setLoadError(message);
        setDetailError(message);
      } finally {
        setActionState((prev) => ({ ...prev, [actionKey]: false }));
      }
    },
    [closeDetailModal, deleteJsonWithToken, fetchToken, selectedRowId]
  );

  const handleDetailFieldChange = useCallback((field, value) => {
    setDetailForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    setSaveNotice("");
  }, []);

  const handleUploadFieldChange = useCallback((field, value) => {
    setUploadForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    setUploadError("");
    setUploadNotice("");
  }, []);

  const handleControlLinkFieldChange = useCallback((field, value) => {
    setControlLinkForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    setControlLinksError("");
    setControlLinksNotice("");
  }, []);

  const handleUploadFileChange = useCallback((event) => {
    const file = event.target.files?.[0] || null;
    setUploadForm((prev) => ({
      ...prev,
      file,
    }));

    setUploadTouched(true);

    const validationMessage = getUploadValidationMessage(file);
    setUploadError(validationMessage);
    setUploadNotice("");
  }, []);

  const resetDetailForm = useCallback(() => {
    setDetailForm(detailFormBaseline);
    setSaveNotice("");
  }, [detailFormBaseline]);

  const resetUploadForm = useCallback(() => {
    setUploadForm({
      file: null,
      changeSummary: "",
      notes: "",
      effectiveDate: "",
    });
    setUploadError("");
    setUploadNotice("");
    setUploadTouched(false);
    if (uploadInputRef.current) {
      uploadInputRef.current.value = "";
    }
  }, []);

  const saveDetailChanges = useCallback(async () => {
    if (!selectedRowId || !selectedCanEdit) return;

    const actionKey = `${selectedRowId}:detail-save`;

    setActionState((prev) => ({ ...prev, [actionKey]: true }));
    setLoadError("");
    setDetailError("");
    setSaveNotice("");

    try {
      const token = await fetchToken();

      const payload = {
        title: detailForm.title.trim(),
        metadata: {
          objective: detailForm.objective.trim(),
          process_name: detailForm.processName.trim(),
          compliance_requirements: parseComplianceRequirements(
            detailForm.complianceRequirementsText
          ),
          display_objective: detailForm.objective.trim(),
          display_process_name: detailForm.processName.trim(),
          display_compliance_requirements: parseComplianceRequirements(
            detailForm.complianceRequirementsText
          ),
          display_latest_version_notes: detailForm.latestVersionNotes.trim(),
        },
      };

      await patchJsonWithToken(
        token,
        `/sops/${selectedRowId}`,
        payload,
        undefined
      );

      if (selectedRow) {
        await refreshSelectedDetail(selectedRow, { preserveTab: true });
      }

      setReloadTick((value) => value + 1);
      setSaveNotice("Changes saved.");
    } catch (error) {
      console.error("Failed to save SOP detail changes", error);
      const message =
        error?.message || "Unable to save SOP changes right now.";
      setLoadError(message);
      setDetailError(message);
    } finally {
      setActionState((prev) => ({ ...prev, [actionKey]: false }));
    }
  }, [
    detailForm,
    fetchToken,
    patchJsonWithToken,
    refreshSelectedDetail,
    selectedCanEdit,
    selectedRow,
    selectedRowId,
  ]);

  const createControlLink = useCallback(async () => {
    if (!selectedRowId) return;

    const controlId = controlLinkForm.controlId.trim();
    if (!controlId) {
      setControlLinksError("Control ID is required.");
      return;
    }

    const priorityValue = Number(controlLinkForm.priority || 100);
    const actionKey = `${selectedRowId}:control-link-create`;

    setActionState((prev) => ({ ...prev, [actionKey]: true }));
    setControlLinksError("");
    setControlLinksNotice("");

    try {
      const token = await fetchToken();
      await postJsonWithToken(
        token,
        `/sops/${selectedRowId}/links`,
        {
          control_ids: [controlId],
          rule_ids: controlLinkForm.ruleId.trim()
            ? [controlLinkForm.ruleId.trim()]
            : [],
          framework: controlLinkForm.framework.trim() || null,
          priority: Number.isFinite(priorityValue) && priorityValue > 0 ? priorityValue : 100,
          notes: controlLinkForm.notes.trim() || null,
        },
        undefined
      );

      setControlLinkForm({
        controlId: "",
        ruleId: "",
        framework: "",
        priority: "100",
        notes: "",
      });
      setPendingRemoveControlLinkId(null);
      setEditingControlLinkId(null);

      if (selectedRow) {
        await refreshSelectedDetail(selectedRow, { preserveTab: true });
      }

      setControlLinksNotice("Control link created.");
      notifyDocumentControlChanged({
        type: "sop_control_link_created",
        sopId: selectedRowId,
        controlId,
      });
    } catch (error) {
      console.error("Failed to create SOP control link", error);
      setControlLinksError(
        error?.message || "Unable to create the SOP-control link right now."
      );
    } finally {
      setActionState((prev) => ({ ...prev, [actionKey]: false }));
    }
  }, [
    controlLinkForm,
    fetchToken,
    notifyDocumentControlChanged,
    postJsonWithToken,
    refreshSelectedDetail,
    selectedRow,
    selectedRowId,
  ]);

  const startEditControlLink = useCallback((link) => {
    if (!link?.id) return;

    setPendingRemoveControlLinkId(null);
    setEditingControlLinkId(link.id);
    setControlLinksError("");
    setControlLinksNotice("");
    setControlLinkForm({
      controlId: link.control_id || "",
      ruleId: link.rule_id || "",
      framework: link.framework || "",
      priority: String(link.priority || 100),
      notes: link.notes || "",
    });
  }, []);

  const cancelEditControlLink = useCallback(() => {
    setEditingControlLinkId(null);
    setControlLinksError("");
    setControlLinksNotice("");
    setControlLinkForm({
      controlId: "",
      ruleId: "",
      framework: "",
      priority: "100",
      notes: "",
    });
  }, []);

  const updateControlLink = useCallback(async () => {
    if (!selectedRowId || !editingControlLinkId) return;

    const controlId = controlLinkForm.controlId.trim();
    if (!controlId) {
      setControlLinksError("Control ID is required.");
      return;
    }

    const priorityValue = Number(controlLinkForm.priority || 100);
    const actionKey = `${selectedRowId}:control-link-update:${editingControlLinkId}`;

    setActionState((prev) => ({ ...prev, [actionKey]: true }));
    setControlLinksError("");
    setControlLinksNotice("");

    try {
      const token = await fetchToken();
      await patchJsonWithToken(
        token,
        `/sops/${selectedRowId}/links/${editingControlLinkId}`,
        {
          control_id: controlId,
          rule_id: controlLinkForm.ruleId.trim() || null,
          framework: controlLinkForm.framework.trim() || null,
          priority: Number.isFinite(priorityValue) && priorityValue > 0 ? priorityValue : 100,
          notes: controlLinkForm.notes.trim() || null,
        },
        undefined
      );

      setEditingControlLinkId(null);
      setControlLinkForm({
        controlId: "",
        ruleId: "",
        framework: "",
        priority: "100",
        notes: "",
      });

      if (selectedRow) {
        await refreshSelectedDetail(selectedRow, { preserveTab: true });
      }

      setControlLinksNotice("Control link updated.");
      notifyDocumentControlChanged({
        type: "sop_control_link_updated",
        sopId: selectedRowId,
        linkId: editingControlLinkId,
        controlId,
      });
    } catch (error) {
      console.error("Failed to update SOP control link", error);
      setControlLinksError(
        error?.message || "Unable to update the SOP-control link right now."
      );
    } finally {
      setActionState((prev) => ({ ...prev, [actionKey]: false }));
    }
  }, [
    controlLinkForm,
    editingControlLinkId,
    fetchToken,
    notifyDocumentControlChanged,
    patchJsonWithToken,
    refreshSelectedDetail,
    selectedRow,
    selectedRowId,
  ]);

  const removeControlLink = useCallback(async (link) => {
    if (!selectedRowId || !link?.id) return;

    if (pendingRemoveControlLinkId !== link.id) {
      setPendingRemoveControlLinkId(link.id);
      setEditingControlLinkId(null);
      setControlLinksError("");
      setControlLinksNotice("");
      return;
    }

    const actionKey = `${selectedRowId}:control-link-remove:${link.id}`;

    setActionState((prev) => ({ ...prev, [actionKey]: true }));
    setControlLinksError("");
    setControlLinksNotice("");

    try {
      const token = await fetchToken();
      await deleteJsonWithToken(
        token,
        `/sops/${selectedRowId}/links/${link.id}`,
        { reason: "Removed from SOP Center." },
        undefined
      );

      if (selectedRow) {
        await refreshSelectedDetail(selectedRow, { preserveTab: true });
      }

      setPendingRemoveControlLinkId(null);
      setControlLinksNotice("Control link removed.");
      notifyDocumentControlChanged({
        type: "sop_control_link_removed",
        sopId: selectedRowId,
        controlId: link.control_id,
        linkId: link.id,
      });
    } catch (error) {
      console.error("Failed to remove SOP control link", error);
      setControlLinksError(
        error?.message || "Unable to remove the SOP-control link right now."
      );
    } finally {
      setActionState((prev) => ({ ...prev, [actionKey]: false }));
    }
  }, [
    deleteJsonWithToken,
    fetchToken,
    notifyDocumentControlChanged,
    pendingRemoveControlLinkId,
    refreshSelectedDetail,
    selectedRow,
    selectedRowId,
  ]);

  const submitRevisedUpload = useCallback(async () => {
    if (!selectedRowId || !selectedRow) return;

    setUploadTouched(true);

    const validationMessage = getUploadValidationMessage(uploadForm.file);
    if (validationMessage) {
      setUploadError(validationMessage);
      return;
    }

    const actionKey = `${selectedRowId}:upload`;
    setActionState((prev) => ({ ...prev, [actionKey]: true }));
    setLoadError("");
    setDetailError("");
    setUploadError("");
    setUploadNotice("");

    try {
      const token = await fetchToken();
      const formData = new FormData();
      formData.append("file", uploadForm.file);
      formData.append(
        "change_summary",
        uploadForm.changeSummary.trim() ||
          "Uploaded revised file"
      );
      formData.append("notes", uploadForm.notes.trim());
      formData.append("set_as_current", "false");
      if (uploadForm.effectiveDate) {
        formData.append("effective_date", uploadForm.effectiveDate);
      }

      const result = await postFormWithToken(
        token,
        `/sops/${selectedRowId}/versions/upload`,
        formData,
        undefined
      );

      setReloadTick((value) => value + 1);

      const refreshedDetail = await forceRefreshSelectedDetail(selectedRow, {
        preserveTab: true,
        nextTab: "details",
        bumpPreview: false,
      });

      setReloadTick((value) => value + 1);
      resetUploadForm();

      const uploadedVersionNumber = result?.version_number || "new";
      const currentVersionNumber =
        refreshedDetail?.current_version_number || selectedRow?.latestVersionNumber || "current";

      setUploadNotice(
        `Version ${uploadedVersionNumber} uploaded as Pending Approval. The current controlled version remains v${currentVersionNumber} until approval.`
      );

      notifyDocumentControlChanged({
        type: "version_uploaded",
        sopId: selectedRowId,
        versionNumber: result?.version_number || null,
        status: "pending approval",
      });
    } catch (error) {
      console.error("Failed to upload revised SOP file", error);
      const message =
        error?.message ||
        "Upload failed. Your existing SOP versions were not changed.";
      setUploadError(message);
    } finally {
      setActionState((prev) => ({ ...prev, [actionKey]: false }));
    }
  }, [
    fetchToken,
    forceRefreshSelectedDetail,
    notifyDocumentControlChanged,
    postFormWithToken,
    resetUploadForm,
    selectedRow,
    selectedRowId,
    uploadForm,
  ]);

  const openApproveDialog = useCallback(
    (version) => {
      if (!selectedCanApproveVersion) {
        setReviewDialog((prev) => ({
          ...prev,
          mode: null,
          version: null,
          error:
            selectedReviewPermissionMessage ||
            "You do not have permission to approve SOP versions.",
        }));
        setDetailError(
          selectedReviewPermissionMessage ||
            "You do not have permission to approve SOP versions."
        );
        return;
      }

      setReviewDialog({
        mode: "approve",
        version,
        reason: "",
        notes: `Approved v${version?.version_number || ""}`.trim(),
        error: "",
      });
    },
    [selectedCanApproveVersion, selectedReviewPermissionMessage]
  );

  const openRejectDialog = useCallback(
    (version) => {
      if (!selectedCanRejectVersion) {
        setReviewDialog((prev) => ({
          ...prev,
          mode: null,
          version: null,
          error:
            selectedReviewPermissionMessage ||
            "You do not have permission to reject SOP versions.",
        }));
        setDetailError(
          selectedReviewPermissionMessage ||
            "You do not have permission to reject SOP versions."
        );
        return;
      }

      setReviewDialog({
        mode: "reject",
        version,
        reason: "",
        notes: "",
        error: "",
      });
    },
    [selectedCanRejectVersion, selectedReviewPermissionMessage]
  );

  const closeReviewDialog = useCallback(() => {
    setReviewDialog({ mode: null, version: null, reason: "", notes: "", error: "" });
  }, []);

  const updateReviewDialogField = useCallback((field, value) => {
    setReviewDialog((prev) => ({ ...prev, [field]: value, error: "" }));
  }, []);

  const submitReviewDecision = useCallback(async () => {
    if (!selectedRowId || !selectedRow || !reviewDialog.version || !reviewDialog.mode) return;

    const version = reviewDialog.version;
    const mode = reviewDialog.mode;
    const reason = reviewDialog.reason.trim();
    const notes = reviewDialog.notes.trim();

    if (mode === "approve" && !selectedCanApproveVersion) {
      setReviewDialog((prev) => ({
        ...prev,
        error:
          selectedReviewPermissionMessage ||
          "You do not have permission to approve SOP versions.",
      }));
      return;
    }

    if (mode === "reject" && !selectedCanRejectVersion) {
      setReviewDialog((prev) => ({
        ...prev,
        error:
          selectedReviewPermissionMessage ||
          "You do not have permission to reject SOP versions.",
      }));
      return;
    }

    if (mode === "reject" && !reason) {
      setReviewDialog((prev) => ({
        ...prev,
        error: "A rejection reason is required for the audit trail.",
      }));
      return;
    }

    const actionKey = `${selectedRowId}:review:${version.id}:${mode}`;
    setActionState((prev) => ({ ...prev, [actionKey]: true }));
    setLoadError("");
    setDetailError("");
    setSaveNotice("");
    setUploadError("");
    setUploadNotice("");

    try {
      const token = await fetchToken();
      const endpoint = `/sops/${selectedRowId}/versions/${version.id}/${mode}`;
      const payload =
        mode === "approve"
          ? {
              approved_by_user_id: userId || undefined,
              change_summary:
                notes ||
                `Approved v${version.version_number || ""}`.trim() ||
                "Approved",
            }
          : {
              reason,
              notes,
              rejected_by_user_id: userId || undefined,
            };

      await postJsonWithToken(token, endpoint, payload, undefined);

      closeReviewDialog();

      setReloadTick((value) => value + 1);

      const refreshed = await forceRefreshSelectedDetail(selectedRow, {
        preserveTab: true,
        nextTab: "details",
        bumpPreview: mode === "approve",
      });

      setReloadTick((value) => value + 1);
      setSaveNotice(
        mode === "approve"
          ? `Version ${version.version_number || ""} approved and promoted to the current controlled version.`
          : `Version ${version.version_number || ""} rejected. The current controlled version was not changed.`
      );

      notifyDocumentControlChanged({
        type: mode === "approve" ? "version_approved" : "version_rejected",
        sopId: selectedRowId,
        versionId: version.id,
        versionNumber: version.version_number || null,
        mode,
      });

      return refreshed;
    } catch (error) {
      console.error(`Failed to ${mode} SOP version`, error);
      const message =
        error?.message ||
        `Unable to ${mode} this SOP version right now.`;
      setReviewDialog((prev) => ({ ...prev, error: message }));
      setDetailError(message);
      return null;
    } finally {
      setActionState((prev) => ({ ...prev, [actionKey]: false }));
    }
  }, [
    closeReviewDialog,
    fetchToken,
    forceRefreshSelectedDetail,
    notifyDocumentControlChanged,
    postJsonWithToken,
    reviewDialog,
    selectedCanApproveVersion,
    selectedCanRejectVersion,
    selectedReviewPermissionMessage,
    selectedRow,
    selectedRowId,
    userId,
  ]);

  const submitAttestation = useCallback(async () => {
    if (!selectedRowId || !selectedRow || !currentVersionForAttestation?.id) return;

    const actionKey = `${selectedRowId}:attest:${currentVersionForAttestation.id}`;
    const requirementId =
      attestationSummary.pendingUserRequirements[0]?.id ||
      attestationSummary.userRequired[0]?.id ||
      null;

    setActionState((prev) => ({ ...prev, [actionKey]: true }));
    setLoadError("");
    setDetailError("");
    setAttestationError("");
    setAttestationNotice("");

    try {
      const token = await fetchToken();
      await postJsonWithToken(
        token,
        `/sops/${selectedRowId}/versions/${currentVersionForAttestation.id}/attest`,
        {
          requirement_id: requirementId || undefined,
          attestation_type:
            attestationSummary.pendingUserRequirements[0]?.requirement_type ||
            attestationSummary.userRequired[0]?.requirement_type ||
            "read_and_understood",
          statement: "I have read, understood, and acknowledge this SOP version.",
          notes: "Acknowledged from SOP Center.",
        },
        undefined
      );

      await forceRefreshSelectedDetail(selectedRow, {
        preserveTab: true,
        nextTab: "details",
      });

      setAttestationNotice("SOP acknowledged. Your attestation has been recorded.");
      notifyDocumentControlChanged({
        type: "sop_attested",
        sopId: selectedRowId,
        versionId: currentVersionForAttestation.id,
      });
    } catch (error) {
      console.error("Failed to attest to SOP version", error);
      const message =
        error?.message || "Unable to acknowledge this SOP version right now.";
      setAttestationError(message);
      setDetailError(message);
    } finally {
      setActionState((prev) => ({ ...prev, [actionKey]: false }));
    }
  }, [
    attestationSummary,
    currentVersionForAttestation,
    fetchToken,
    forceRefreshSelectedDetail,
    notifyDocumentControlChanged,
    postJsonWithToken,
    selectedRow,
    selectedRowId,
  ]);

  let emptyMessage =
    activeQueueFilter === "pending_approval"
      ? "No SOP revisions are waiting for approval right now."
      : activeQueueFilter === "pending_acknowledgment"
      ? "No SOP acknowledgments are pending for you right now."
      : activeQueueFilter === "overdue"
      ? "No overdue SOPs found."
      : activeQueueFilter === "archived"
      ? "No archived SOPs found."
      : activeQueueFilter === "active_findings"
      ? "No SOPs are currently tied to active findings."
      : "No items found. Try adjusting your filters or generating a new document.";

  if (categoryFilter === "sop" && activeQueueFilter === "all") {
    emptyMessage =
      "No SOPs yet. Generate your first SOP and it will appear here.";
  } else if (categoryFilter === "compliance" && activeQueueFilter === "all") {
    emptyMessage =
      "No compliance records yet. Once you run compliance checks, they’ll appear here.";
  } else if (categoryFilter === "checklist" && activeQueueFilter === "all") {
    emptyMessage =
      "No checklists yet. Checklist templates are coming soon to your Library.";
  } else if (categoryFilter === "policy" && activeQueueFilter === "all") {
    emptyMessage =
      "No policies yet. Policy documents will show up here as you add them.";
  }

  const renderTable = () => (
    <div style={{ minWidth: 0 }}>
      {activeQueueFilter === "pending_approval" ? (
        <div
          style={{
            marginBottom: "0.9rem",
            padding: "0.85rem 1rem",
            borderRadius: "16px",
            background: "rgba(180, 83, 9, 0.06)",
            border: "1px solid rgba(180, 83, 9, 0.18)",
            color: "var(--color-text-secondary)",
            lineHeight: 1.6,
          }}
        >
          Showing SOPs with revision candidates waiting for approval. Open a row,
          review Version History, then approve or reject the pending version.
        </div>
      ) : null}

      {activeQueueFilter === "pending_acknowledgment" ? (
        <div
          style={{
            marginBottom: "0.9rem",
            padding: "0.85rem 1rem",
            borderRadius: "16px",
            background: "rgba(37, 99, 235, 0.06)",
            border: "1px solid rgba(37, 99, 235, 0.18)",
            color: "var(--color-text-secondary)",
            lineHeight: 1.6,
          }}
        >
          <strong>Pending acknowledgments:</strong> Showing only effective SOPs
          that still need your read-and-understood confirmation. Open a row,
          review the controlled version, then acknowledge it from the Release &
          acknowledgments card.
        </div>
      ) : null}

      {activeQueueFilter === "overdue" ? (
        <div
          style={{
            marginBottom: "0.9rem",
            padding: "0.85rem 1rem",
            borderRadius: "16px",
            background: "rgba(185, 28, 28, 0.05)",
            border: "1px solid rgba(185, 28, 28, 0.18)",
            color: "var(--color-text-secondary)",
            lineHeight: 1.6,
          }}
        >
          Showing SOPs returned by the backend overdue/outdated detector. Open a
          row to review the controlled document and decide whether it should be
          revised, regenerated, or archived.
        </div>
      ) : null}

      <table className="library-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Type</th>
            <th>Created</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((row) => {
            const normalizedStatus = (row.status || "").toLowerCase();
            const canSubmit = normalizedStatus === "draft";
            const canArchive =
              activeQueueFilter !== "pending_approval" &&
              [
                "draft",
                "pending approval",
                "approved",
                "active",
              ].includes(normalizedStatus);
            const canRename = ["draft", "pending approval"].includes(
              normalizedStatus
            );
            const canDelete = normalizedStatus === "draft";

            const submitBusy = Boolean(actionState[`${row.id}:submit`]);
            const archiveBusy = Boolean(actionState[`${row.id}:archive`]);
            const renameBusy = Boolean(actionState[`${row.id}:rename`]);
            const deleteBusy = Boolean(actionState[`${row.id}:delete`]);

            const isEditing = editingRowId === row.id;
            const isConfirmingDelete = deleteConfirmRowId === row.id;
            const isConfirmingArchive = archiveConfirmRowId === row.id;
            const isSelected = selectedRowId === row.id;

            return (
              <tr
                key={row.id}
                onClick={() => handleRowSelect(row)}
                style={{
                  cursor: isEditing ? "default" : "pointer",
                  background: isSelected
                    ? "color-mix(in srgb, var(--color-primary, #0C8F5D), transparent 96%)"
                    : undefined,
                }}
              >
                <td>
                  {isEditing ? (
                    <div className="library-title-edit" onClick={stopRowClick}>
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        className="library-title-input"
                      />
                      <div className="library-title-edit-actions">
                        <button
                          type="button"
                          className="library-link library-link--compact"
                          onClick={() => saveRename(row)}
                          disabled={renameBusy}
                        >
                          {renameBusy ? "Saving…" : "Save"}
                        </button>
                        <button
                          type="button"
                          className="library-link library-link--compact"
                          onClick={cancelRename}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="library-title-row">
                        <span>{row.title}</span>
                        {canRename ? (
                          <button
                            type="button"
                            onClick={(event) => {
                              stopRowClick(event);
                              beginRename(row);
                            }}
                            aria-label={`Rename ${row.title}`}
                            title="Rename SOP"
                            className="library-title-icon-button"
                          >
                            ✏️
                          </button>
                        ) : null}
                      </div>
                      {row.code ? (
                        <div className="library-row-subtext">{row.code}</div>
                      ) : null}
                    </>
                  )}
                </td>
                <td>
                  <TypeBadge type={row.type} />
                </td>
                <td>{formatDate(row.createdAt)}</td>
                <td>
                  <StatusBadge
                    status={row.status}
                    isOutdated={row.isOutdated}
                  />
                </td>
                <td className="library-actions-cell" onClick={stopRowClick}>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.38rem",
                      flex: "0 1 auto",
                      minWidth: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <DownloadAction
                      url={row.pdfUrl}
                      label="PDF"
                      icon="📄"
                      onClick={stopRowClick}
                    />
                    <DownloadAction
                      url={row.docxUrl}
                      label="Word"
                      icon="📝"
                      onClick={stopRowClick}
                    />

                    {canSubmit ? (
                      <button
                        type="button"
                        className="library-link library-link--compact"
                        onClick={() => performLifecycleAction(row, "submit")}
                        disabled={submitBusy}
                      >
                        {submitBusy ? "…" : "Submit"}
                      </button>
                    ) : null}

                    {canArchive ? (
                      <div
                        className="library-delete-anchor"
                        ref={isConfirmingArchive ? archivePopoverRef : null}
                        style={{ display: "inline-flex", marginLeft: "0.1rem" }}
                      >
                        <button
                          type="button"
                          className="library-link--danger library-link--compact"
                          onClick={(event) => {
                            stopRowClick(event);
                            confirmArchive(row.id);
                          }}
                          disabled={archiveBusy}
                        >
                          {archiveBusy ? "…" : "Archive"}
                        </button>

                        {isConfirmingArchive ? (
                          <div
                            className="library-delete-popover"
                            onClick={stopRowClick}
                          >
                            <div className="library-delete-popover-text">
                              Archive this SOP?
                            </div>
                            <div className="library-delete-popover-actions">
                              <button
                                type="button"
                                className="library-link library-link--compact"
                                onClick={cancelArchive}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="library-link--danger library-link--compact"
                                onClick={() => performLifecycleAction(row, "archive")}
                                disabled={archiveBusy}
                              >
                                {archiveBusy ? "…" : "Confirm"}
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  {canDelete ? (
                    <div
                      className="library-delete-anchor"
                      ref={isConfirmingDelete ? deletePopoverRef : null}
                      style={{ marginLeft: "0.35rem" }}
                    >
                      <button
                        type="button"
                        onClick={(event) => {
                          stopRowClick(event);
                          confirmDelete(row.id);
                        }}
                        aria-label={`Delete ${row.title}`}
                        title="Delete draft SOP"
                        className="library-delete-icon-button"
                      >
                        🗑️
                      </button>

                      {isConfirmingDelete ? (
                        <div
                          className="library-delete-popover"
                          onClick={stopRowClick}
                        >
                          <div className="library-delete-popover-text">
                            Delete this draft SOP?
                          </div>
                          <div className="library-delete-popover-actions">
                            <button
                              type="button"
                              className="library-link library-link--compact"
                              onClick={cancelDelete}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="library-link--danger library-link--compact"
                              onClick={() => performDelete(row)}
                              disabled={deleteBusy}
                            >
                              {deleteBusy ? "…" : "Confirm"}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderFieldCard = (label, content) => (
    <div
      style={{
        border:
          "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 90%)",
        borderRadius: "20px",
        padding: "1rem",
        background:
          "color-mix(in srgb, var(--color-surface, #ffffff), transparent 2%)",
      }}
    >
      <div
        style={{
          fontSize: "0.72rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "var(--color-text-secondary)",
          marginBottom: "0.5rem",
        }}
      >
        {label}
      </div>
      {content}
    </div>
  );

  const modalInputStyle = {
    width: "100%",
    minHeight: "42px",
    borderRadius: "12px",
    border:
      "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 84%)",
    padding: "0.7rem 0.8rem",
    fontSize: "0.98rem",
    fontFamily: "var(--font-sans)",
    color: "var(--color-text)",
    background: "var(--color-surface, #ffffff)",
    outline: "none",
  };

  const modalTextareaStyle = {
    ...modalInputStyle,
    minHeight: "130px",
    resize: "vertical",
    lineHeight: 1.6,
  };

  const renderSaveStateChip = () => {
    if (!selectedCanEdit) return null;

    const isSaving = Boolean(actionState[`${selectedRowId}:detail-save`]);

    let label = "All changes saved";
    let fg = "#64748b";
    let bg = "rgba(100, 116, 139, 0.08)";
    let border = "rgba(100, 116, 139, 0.18)";

    if (isSaving) {
      label = "Saving changes…";
      fg = "#0C8F5D";
      bg = "rgba(12, 143, 93, 0.08)";
      border = "rgba(12, 143, 93, 0.18)";
    } else if (isDetailDirty) {
      label = "Unsaved changes";
      fg = "#b45309";
      bg = "rgba(180, 83, 9, 0.08)";
      border = "rgba(180, 83, 9, 0.18)";
    } else if (saveNotice) {
      label = "Saved";
      fg = "#0C8F5D";
      bg = "rgba(12, 143, 93, 0.08)";
      border = "rgba(12, 143, 93, 0.18)";
    }

    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.4rem",
          padding: "0.45rem 0.7rem",
          borderRadius: "999px",
          fontSize: "0.84rem",
          fontWeight: 700,
          color: fg,
          background: bg,
          border: `1px solid ${border}`,
        }}
      >
        {label}
      </div>
    );
  };

  const renderUploadPanel = () => {
    const uploadBusy = Boolean(actionState[`${selectedRowId}:upload`]);
    const uploadValidationMessage = uploadTouched
      ? getUploadValidationMessage(uploadForm.file)
      : "";
    const latestVersion = detailView?.latestVersion || null;
    const latestVersionMeta =
      latestVersion?.metadata && typeof latestVersion.metadata === "object"
        ? latestVersion.metadata
        : {};
    const previewStatus = getVersionPreviewStatus(latestVersion, latestVersionMeta);

    return (
      <div
        style={{
          border:
            "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 90%)",
          borderRadius: "18px",
          padding: "1rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.85rem",
          background:
            "color-mix(in srgb, var(--color-surface, #ffffff), transparent 1%)",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "0.82rem",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--color-text-secondary)",
              marginBottom: "0.35rem",
            }}
          >
            Upload revised file
          </div>
          <div
            style={{
              color: "var(--color-text-secondary)",
              lineHeight: 1.7,
              fontSize: "0.95rem",
            }}
          >
            Upload a revised Word or PDF file to create a new controlled SOP
            version. The upload enters Pending Approval and does not replace the
            current controlled version until it is approved.
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.85rem",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
            <label
              style={{
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "var(--color-text-secondary)",
              }}
            >
              File
            </label>
            <input
              ref={uploadInputRef}
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleUploadFileChange}
              disabled={uploadBusy}
              style={{ ...modalInputStyle, padding: "0.55rem 0.75rem" }}
            />
            <div
              style={{
                fontSize: "0.82rem",
                color: "var(--color-text-secondary)",
              }}
            >
              Supported: PDF, DOCX · Max size:{" "}
              {Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
            <label
              style={{
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "var(--color-text-secondary)",
              }}
            >
              Change summary
            </label>
            <input
              type="text"
              value={uploadForm.changeSummary}
              onChange={(e) =>
                handleUploadFieldChange("changeSummary", e.target.value)
              }
              placeholder="Describe what changed in this revision"
              disabled={uploadBusy}
              style={modalInputStyle}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
            <label
              style={{
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "var(--color-text-secondary)",
              }}
            >
              Revision notes
            </label>
            <textarea
              value={uploadForm.notes}
              onChange={(e) => handleUploadFieldChange("notes", e.target.value)}
              placeholder="Optional notes for audit trail"
              disabled={uploadBusy}
              style={{ ...modalTextareaStyle, minHeight: "110px" }}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
            <label
              style={{
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "var(--color-text-secondary)",
              }}
            >
              Effective date
            </label>
            <input
              type="date"
              value={uploadForm.effectiveDate}
              onChange={(e) =>
                handleUploadFieldChange("effectiveDate", e.target.value)
              }
              disabled={uploadBusy}
              style={modalInputStyle}
            />

            {previewStatus === "processing" ? (
              <div
                style={{
                  border: "1px solid rgba(59, 130, 246, 0.16)",
                  borderRadius: "14px",
                  padding: "0.8rem 0.9rem",
                  background: "rgba(59, 130, 246, 0.05)",
                  color: "#1d4ed8",
                  lineHeight: 1.6,
                  fontSize: "0.9rem",
                }}
              >
                Preview is processing. Download is available now.
              </div>
            ) : null}
          </div>
        </div>

        {uploadError || uploadValidationMessage ? (
          <div
            style={{
              border: "1px solid rgba(239, 68, 68, 0.18)",
              borderRadius: "14px",
              padding: "0.8rem 0.9rem",
              background: "rgba(239, 68, 68, 0.04)",
              color: "#b91c1c",
              lineHeight: 1.6,
              fontSize: "0.92rem",
            }}
          >
            {uploadError || uploadValidationMessage}
          </div>
        ) : null}

        {uploadNotice ? (
          <div
            style={{
              border: "1px solid rgba(12, 143, 93, 0.16)",
              borderRadius: "14px",
              padding: "0.8rem 0.9rem",
              background: "rgba(12, 143, 93, 0.06)",
              color: "#0C8F5D",
              lineHeight: 1.6,
              fontSize: "0.92rem",
              fontWeight: 600,
            }}
          >
            {uploadNotice}
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "0.75rem",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              color: "var(--color-text-secondary)",
              fontSize: "0.88rem",
              lineHeight: 1.6,
            }}
          >
            Versions are immutable. Uploading a revised file creates a new
            Pending Approval version, preserves prior history, and leaves the
            current controlled version unchanged until approval.
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              className="library-link library-link--compact"
              onClick={resetUploadForm}
              disabled={uploadBusy}
            >
              Reset
            </button>
            <button
              type="button"
              className="library-link library-link--compact"
              onClick={submitRevisedUpload}
              disabled={uploadBusy}
              title="Upload revised file"
            >
              {uploadBusy ? "Uploading…" : "Upload revised file"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderLinkedControlsCard = () => {
    const createBusy = Boolean(actionState[`${selectedRowId}:control-link-create`]);
    const hasLinks = Array.isArray(controlLinks) && controlLinks.length > 0;

    return renderFieldCard(
      "Linked Controls",
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div
          style={{
            fontSize: "0.93rem",
            color: "var(--color-text-secondary)",
            lineHeight: 1.7,
          }}
        >
          Link this SOP to controls and posture rules so findings, alerts, and
          drift views can show the governing procedure and remediation path.
          Existing links stay listed above; use the form below to add another link.
        </div>

        {controlLinksError ? (
          <div
            style={{
              border: "1px solid rgba(239, 68, 68, 0.18)",
              borderRadius: "14px",
              padding: "0.85rem",
              background: "rgba(239, 68, 68, 0.04)",
              color: "#b91c1c",
              lineHeight: 1.6,
            }}
          >
            {controlLinksError}
          </div>
        ) : null}

        {controlLinksNotice ? (
          <div
            style={{
              border: "1px solid rgba(12, 143, 93, 0.16)",
              borderRadius: "14px",
              padding: "0.85rem",
              background: "rgba(12, 143, 93, 0.06)",
              color: "#0C8F5D",
              fontWeight: 700,
            }}
          >
            {controlLinksNotice}
          </div>
        ) : null}

        <div
          style={{
            border:
              "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 92%)",
            borderRadius: "16px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(120px, 0.95fr) minmax(180px, 1.1fr) minmax(90px, 0.55fr) 68px minmax(170px, 1.1fr) 176px",
              gap: "0.75rem",
              alignItems: "center",
              padding: "0.65rem 0.9rem",
              background:
                "color-mix(in srgb, var(--color-text, #0f172a), transparent 97%)",
              color: "var(--color-text-secondary)",
              fontSize: "0.72rem",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            <span>Control</span>
            <span>Rule</span>
            <span>Framework</span>
            <span>Priority</span>
            <span>Notes</span>
            <span>Action</span>
          </div>

          {controlLinksLoading ? (
            <div
              style={{
                padding: "0.9rem",
                color: "var(--color-text-secondary)",
              }}
            >
              Loading linked controls…
            </div>
          ) : hasLinks ? (
            controlLinks.map((link) => {
              const removeKey = `${selectedRowId}:control-link-remove:${link.id}`;
              const removeBusy = Boolean(actionState[removeKey]);

              return (
                <div
                  key={link.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(120px, 0.95fr) minmax(180px, 1.1fr) minmax(90px, 0.55fr) 68px minmax(170px, 1.1fr) 176px",
                    gap: "0.75rem",
                    alignItems: "center",
                    padding: "0.58rem 0.9rem",
                    borderTop:
                      "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 94%)",
                    fontSize: "0.84rem",
                  }}
                >
                  <strong
                    style={{
                      color: "var(--color-text)",
                      minWidth: 0,
                      overflowWrap: "anywhere",
                      lineHeight: 1.45,
                    }}
                  >
                    {link.control_id || "--"}
                  </strong>
                  <span
                    style={{
                      color: "var(--color-text-secondary)",
                      minWidth: 0,
                      overflowWrap: "anywhere",
                      lineHeight: 1.45,
                    }}
                  >
                    {link.rule_id || "Any rule"}
                  </span>
                  <span
                    style={{
                      color: "var(--color-text-secondary)",
                      minWidth: 0,
                      overflowWrap: "anywhere",
                      lineHeight: 1.45,
                    }}
                  >
                    {link.framework || "Any"}
                  </span>
                  <span
                    style={{
                      color: "var(--color-text-secondary)",
                      minWidth: 0,
                      lineHeight: 1.45,
                    }}
                  >
                    {link.priority ?? "--"}
                  </span>
                  <span
                    style={{
                      color: "var(--color-text-secondary)",
                      lineHeight: 1.5,
                      minWidth: 0,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {link.notes || "No notes"}
                  </span>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-start",
                      gap: "0.35rem",
                      flexWrap: "wrap",
                    }}
                  >
                    {editingControlLinkId === link.id ? (
                      <>
                        <button
                          type="button"
                          className="library-link library-link--compact"
                          onClick={cancelEditControlLink}
                          disabled={Boolean(actionState[`${selectedRowId}:control-link-update:${link.id}`])}
                          title="Cancel edit"
                          style={{
                            width: "68px",
                            justifyContent: "center",
                            minHeight: "34px",
                            paddingLeft: "0.45rem",
                            paddingRight: "0.45rem",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="library-link library-link--compact"
                          onClick={updateControlLink}
                          disabled={Boolean(actionState[`${selectedRowId}:control-link-update:${link.id}`])}
                          title="Save link changes"
                          style={{
                            width: "68px",
                            justifyContent: "center",
                            minHeight: "34px",
                            paddingLeft: "0.45rem",
                            paddingRight: "0.45rem",
                          }}
                        >
                          {actionState[`${selectedRowId}:control-link-update:${link.id}`] ? "…" : "Save"}
                        </button>
                      </>
                    ) : pendingRemoveControlLinkId === link.id ? (
                      <>
                        <button
                          type="button"
                          className="library-link library-link--compact"
                          onClick={() => setPendingRemoveControlLinkId(null)}
                          disabled={removeBusy}
                          title="Cancel remove"
                          style={{
                            width: "68px",
                            justifyContent: "center",
                            minHeight: "34px",
                            paddingLeft: "0.45rem",
                            paddingRight: "0.45rem",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="library-link--danger library-link--compact"
                          onClick={() => removeControlLink(link)}
                          disabled={removeBusy}
                          title="Confirm remove"
                          style={{
                            width: "76px",
                            justifyContent: "center",
                            minHeight: "34px",
                            paddingLeft: "0.45rem",
                            paddingRight: "0.45rem",
                          }}
                        >
                          {removeBusy ? "…" : "Confirm"}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="library-link library-link--compact"
                          onClick={() => startEditControlLink(link)}
                          disabled={removeBusy}
                          title="Edit this SOP-control link"
                          style={{
                            width: "64px",
                            justifyContent: "center",
                            minHeight: "34px",
                            paddingLeft: "0.45rem",
                            paddingRight: "0.45rem",
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="library-link--danger library-link--compact"
                          onClick={() => removeControlLink(link)}
                          disabled={removeBusy}
                          title="Remove this SOP-control link"
                          style={{
                            width: "86px",
                            justifyContent: "center",
                            minHeight: "34px",
                            paddingLeft: "0.45rem",
                            paddingRight: "0.45rem",
                          }}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div
              style={{
                padding: "0.9rem",
                color: "var(--color-text-secondary)",
                lineHeight: 1.6,
              }}
            >
              No controls linked yet. Add the first control below.
            </div>
          )}
        </div>

        {selectedCanEdit ? (
          <div
            style={{
              border:
                "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 92%)",
              borderRadius: "16px",
              padding: "1rem",
              background:
                "color-mix(in srgb, var(--color-text, #0f172a), transparent 98%)",
              display: "flex",
              flexDirection: "column",
              gap: "0.85rem",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 0.75fr 0.45fr",
                gap: "0.75rem",
              }}
            >
              <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                <span style={{ fontSize: "0.72rem", fontWeight: 800, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Control ID
                </span>
                <input
                  type="text"
                  value={controlLinkForm.controlId}
                  onChange={(e) => handleControlLinkFieldChange("controlId", e.target.value)}
                  style={modalInputStyle}
                  placeholder="SOC2-CC6.1"
                />
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                <span style={{ fontSize: "0.72rem", fontWeight: 800, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Rule ID
                </span>
                <input
                  type="text"
                  value={controlLinkForm.ruleId}
                  onChange={(e) => handleControlLinkFieldChange("ruleId", e.target.value)}
                  style={modalInputStyle}
                  placeholder="IAM_USER_MFA"
                />
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                <span style={{ fontSize: "0.72rem", fontWeight: 800, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Framework
                </span>
                <input
                  type="text"
                  value={controlLinkForm.framework}
                  onChange={(e) => handleControlLinkFieldChange("framework", e.target.value)}
                  style={modalInputStyle}
                  placeholder="SOC2"
                />
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                <span style={{ fontSize: "0.72rem", fontWeight: 800, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Priority
                </span>
                <input
                  type="number"
                  min="1"
                  value={controlLinkForm.priority}
                  onChange={(e) => handleControlLinkFieldChange("priority", e.target.value)}
                  style={modalInputStyle}
                />
              </label>
            </div>

            <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
              <span style={{ fontSize: "0.72rem", fontWeight: 800, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Notes
              </span>
              <textarea
                value={controlLinkForm.notes}
                onChange={(e) => handleControlLinkFieldChange("notes", e.target.value)}
                style={{ ...modalTextareaStyle, minHeight: "70px" }}
                placeholder="Why this SOP governs the control or rule."
              />
            </label>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              {editingControlLinkId ? (
                <button
                  type="button"
                  className="library-link library-link--compact"
                  onClick={cancelEditControlLink}
                  disabled={Boolean(actionState[`${selectedRowId}:control-link-update:${editingControlLinkId}`])}
                >
                  Cancel Edit
                </button>
              ) : (
                <button
                  type="button"
                  className="library-link library-link--compact"
                  onClick={() => {
                    setControlLinkForm({
                      controlId: "",
                      ruleId: "",
                      framework: "",
                      priority: "100",
                      notes: "",
                    });
                    setPendingRemoveControlLinkId(null);
                    setEditingControlLinkId(null);
                  }}
                  disabled={createBusy}
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                className="library-link library-link--compact"
                onClick={editingControlLinkId ? updateControlLink : createControlLink}
                disabled={
                  createBusy ||
                  Boolean(
                    editingControlLinkId &&
                      actionState[`${selectedRowId}:control-link-update:${editingControlLinkId}`]
                  ) ||
                  !controlLinkForm.controlId.trim()
                }
              >
                {editingControlLinkId
                  ? actionState[`${selectedRowId}:control-link-update:${editingControlLinkId}`]
                    ? "Saving…"
                    : "Save Link"
                  : createBusy
                  ? "Linking…"
                  : "Link Control"}
              </button>
            </div>
          </div>
        ) : (
          <div
            style={{
              color: "var(--color-text-secondary)",
              fontSize: "0.9rem",
              lineHeight: 1.6,
            }}
          >
            Linked controls are read-only for archived or historical records.
          </div>
        )}
      </div>
    );
  };

  const renderReleaseAndAttestationCard = () => {
    const currentVersionNumber =
      selectedDetail?.current_version_number ||
      currentVersionForAttestation?.version_number ||
      "--";
    const attestBusy = Boolean(
      currentVersionForAttestation?.id &&
        actionState[`${selectedRowId}:attest:${currentVersionForAttestation.id}`]
    );
    const releaseTone = getReleaseStatusTone(currentReleaseStatus);
    const releaseLabel = getReleaseStatusLabel(currentReleaseStatus);
    const requirement = attestationSummary.userRequired[0] || attestationRequirements[0] || null;
    const acknowledgedAt = attestationSummary.userAttestations[0]?.attested_at || null;

    return renderFieldCard(
      "Release & acknowledgments",
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
            gap: "0.75rem",
          }}
        >
          <div
            style={{
              border:
                "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 92%)",
              borderRadius: "16px",
              padding: "0.9rem",
            }}
          >
            <div
              style={{
                fontSize: "0.72rem",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "var(--color-text-secondary)",
                marginBottom: "0.4rem",
              }}
            >
              Release status
            </div>
            <InlineControlPill tone={releaseTone}>{releaseLabel}</InlineControlPill>
          </div>

          <div
            style={{
              border:
                "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 92%)",
              borderRadius: "16px",
              padding: "0.9rem",
            }}
          >
            <div
              style={{
                fontSize: "0.72rem",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "var(--color-text-secondary)",
                marginBottom: "0.4rem",
              }}
            >
              Effective date
            </div>
            <div style={{ fontWeight: 800, color: "var(--color-text)" }}>
              {currentEffectiveDate ? formatDate(currentEffectiveDate) : "Immediate"}
            </div>
          </div>

          <div
            style={{
              border:
                "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 92%)",
              borderRadius: "16px",
              padding: "0.9rem",
            }}
          >
            <div
              style={{
                fontSize: "0.72rem",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "var(--color-text-secondary)",
                marginBottom: "0.4rem",
              }}
            >
              Attestable
            </div>
            <InlineControlPill tone={currentIsAttestable ? "active" : "historical"}>
              {currentIsAttestable ? "Ready" : "Blocked"}
            </InlineControlPill>
          </div>

          <div
            style={{
              border:
                "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 92%)",
              borderRadius: "16px",
              padding: "0.9rem",
            }}
          >
            <div
              style={{
                fontSize: "0.72rem",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "var(--color-text-secondary)",
                marginBottom: "0.4rem",
              }}
            >
              My acknowledgment
            </div>
            <InlineControlPill tone={attestationSummary.hasUserAttested ? "active" : "pending"}>
              {attestationSummary.hasUserAttested ? "Recorded" : "Needed"}
            </InlineControlPill>
          </div>
        </div>

        {currentVersionForAttestation?.release_notes ? (
          <div
            style={{
              color: "var(--color-text-secondary)",
              lineHeight: 1.65,
              whiteSpace: "pre-wrap",
            }}
          >
            <strong style={{ color: "var(--color-text)" }}>Release notes:</strong>{" "}
            {currentVersionForAttestation.release_notes}
          </div>
        ) : null}

        {attestationLoading ? (
          <div style={{ color: "var(--color-text-secondary)", lineHeight: 1.7 }}>
            Loading acknowledgment status…
          </div>
        ) : attestationError ? (
          <div
            style={{
              border: "1px solid rgba(239, 68, 68, 0.18)",
              borderRadius: "14px",
              padding: "0.85rem",
              background: "rgba(239, 68, 68, 0.04)",
              color: "#b91c1c",
              lineHeight: 1.6,
            }}
          >
            {attestationError}
          </div>
        ) : (
          <div
            style={{
              border:
                "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 92%)",
              borderRadius: "18px",
              padding: "1rem",
              background:
                attestationSummary.hasUserAttested
                  ? "rgba(12, 143, 93, 0.035)"
                  : currentIsAttestable
                  ? "rgba(180, 83, 9, 0.035)"
                  : "color-mix(in srgb, var(--color-text, #0f172a), transparent 98%)",
              display: "flex",
              justifyContent: "space-between",
              gap: "1rem",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 0, flex: "1 1 360px" }}>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                <strong style={{ color: "var(--color-text)" }}>
                  v{currentVersionNumber} acknowledgment
                </strong>
                {requirement ? (
                  <InlineControlPill tone="neutral">
                    {getRequirementLabel(requirement)}
                  </InlineControlPill>
                ) : null}
                {attestationSummary.hasUserAttested ? (
                  <InlineControlPill tone="active">Completed</InlineControlPill>
                ) : requirement?.due_at ? (
                  <InlineControlPill tone="pending">
                    Due {formatDate(requirement.due_at)}
                  </InlineControlPill>
                ) : null}
              </div>

              <div
                style={{
                  marginTop: "0.45rem",
                  color: "var(--color-text-secondary)",
                  lineHeight: 1.65,
                  fontSize: "0.92rem",
                }}
              >
                {attestationSummary.hasUserAttested
                  ? `Acknowledged ${formatDateTime(acknowledgedAt)} by ${actorLabel(
                      attestationSummary.userAttestations[0]?.attested_by
                    )}.`
                  : currentIsAttestable
                  ? "This SOP is effective and ready for acknowledgment. Recording the acknowledgment creates an audit event."
                  : "This SOP cannot be acknowledged yet because it is not effective."}
              </div>

              {attestationSummary.hasUserAttested ? (
                <div
                  style={{
                    marginTop: "0.55rem",
                    border: "1px solid rgba(12, 143, 93, 0.14)",
                    borderRadius: "14px",
                    padding: "0.7rem 0.8rem",
                    background: "rgba(12, 143, 93, 0.045)",
                    color: "var(--color-text-secondary)",
                    fontSize: "0.88rem",
                    lineHeight: 1.55,
                  }}
                >
                  <strong style={{ color: "#0C8F5D" }}>Compliance note:</strong>{" "}
                  Requirement completed and retained in the audit trail
                  {requirement?.due_at ? ` · original due date ${formatDate(requirement.due_at)}` : ""}.
                </div>
              ) : attestationSummary.totalActiveRequirements ? (
                <div
                  style={{
                    marginTop: "0.4rem",
                    color: "var(--color-text-secondary)",
                    fontSize: "0.86rem",
                  }}
                >
                  Active requirements: {attestationSummary.totalActiveRequirements} · Recorded attestations:{" "}
                  {attestationSummary.totalAttestations}
                </div>
              ) : null}
            </div>

            {!attestationSummary.hasUserAttested ? (
              <button
                type="button"
                className="library-link library-link--compact"
                onClick={submitAttestation}
                disabled={!currentIsAttestable || attestBusy}
                title={
                  currentIsAttestable
                    ? "Acknowledge this effective SOP version."
                    : "This SOP version is not effective yet."
                }
              >
                {attestBusy ? "Recording…" : "Acknowledge SOP"}
              </button>
            ) : (
              <InlineControlPill tone="active">Audit recorded</InlineControlPill>
            )}
          </div>
        )}

        {attestationNotice ? (
          <div
            style={{
              border: "1px solid rgba(12, 143, 93, 0.16)",
              borderRadius: "14px",
              padding: "0.85rem",
              background: "rgba(12, 143, 93, 0.06)",
              color: "#0C8F5D",
              fontWeight: 700,
            }}
          >
            {attestationNotice}
          </div>
        ) : null}
      </div>
    );
  };

  const renderVersionHistoryCard = () => {
    const currentVersionId = selectedDetail?.current_version_id || null;
    const currentVersionNumber = selectedDetail?.current_version_number || null;
    const pendingVersions = versionHistory.filter(
      (version) => normalizeStatusValue(version.status) === "pending approval"
    );

    return renderFieldCard(
      "Version history",
      <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
        <div
          style={{
            border: "1px solid rgba(12, 143, 93, 0.16)",
            borderRadius: "16px",
            padding: "0.9rem 1rem",
            background: "rgba(12, 143, 93, 0.045)",
            color: "var(--color-text)",
            lineHeight: 1.7,
            fontSize: "0.93rem",
          }}
        >
          <strong>Current controlled version:</strong>{" "}
          {currentVersionNumber ? `v${currentVersionNumber}` : "Not approved yet"}.
          Main preview/download actions use the current controlled version.
          Pending revisions remain visible here but are not official until approval.
          {pendingVersions.length ? (
            <div style={{ marginTop: "0.45rem", color: "#b45309", fontWeight: 700 }}>
              {pendingVersions.length} revision{pendingVersions.length === 1 ? "" : "s"} awaiting approval.
            </div>
          ) : null}
        </div>

        {renderUploadPanel()}

        {versionHistory.length ? (
          versionHistory.map((version) => {
            const versionMeta =
              version?.metadata && typeof version.metadata === "object"
                ? version.metadata
                : {};
            const sourceType = getVersionSourceType(versionMeta);
            const fileType = getVersionFileType(version, versionMeta);
            const previewStatus = getVersionPreviewStatus(version, versionMeta);
            const normalizedVersionStatus = normalizeStatusValue(version.status);
            const isCurrent = currentVersionId
              ? version.id === currentVersionId
              : versionHistory[0]?.id === version.id;
            const isPending = normalizedVersionStatus === "pending approval";
            const isRejected = normalizedVersionStatus === "rejected";
            const isSuperseded = normalizedVersionStatus === "superseded";
            const approveBusy = Boolean(actionState[`${selectedRowId}:review:${version.id}:approve`]);
            const rejectBusy = Boolean(actionState[`${selectedRowId}:review:${version.id}:reject`]);

            const cardTone = isCurrent
              ? "active"
              : isPending
              ? "pending"
              : isRejected
              ? "rejected"
              : isSuperseded
              ? "historical"
              : "neutral";

            const borderColor =
              cardTone === "active"
                ? "rgba(12, 143, 93, 0.24)"
                : cardTone === "pending"
                ? "rgba(180, 83, 9, 0.22)"
                : cardTone === "rejected"
                ? "rgba(239, 68, 68, 0.2)"
                : "color-mix(in srgb, var(--color-text, #0f172a), transparent 92%)";

            const backgroundColor =
              cardTone === "active"
                ? "rgba(12, 143, 93, 0.035)"
                : cardTone === "pending"
                ? "rgba(180, 83, 9, 0.035)"
                : cardTone === "rejected"
                ? "rgba(239, 68, 68, 0.025)"
                : "color-mix(in srgb, var(--color-text, #0f172a), transparent 98%)";

            return (
              <div
                key={version.id || `${version.version_number}-${version.created_at}`}
                style={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: "16px",
                  padding: "0.95rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.65rem",
                  background: backgroundColor,
                  boxShadow: isCurrent
                    ? "0 6px 18px rgba(12, 143, 93, 0.06)"
                    : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "1rem",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 800,
                        color: "var(--color-text)",
                      }}
                    >
                      v{version.version_number || "--"}
                    </div>

                    {isCurrent ? (
                      <InlineControlPill tone="active">
                        Current Controlled Version
                      </InlineControlPill>
                    ) : null}

                    {isPending ? (
                      <InlineControlPill tone="pending">
                        Not Official Yet
                      </InlineControlPill>
                    ) : null}

                    {isRejected ? (
                      <InlineControlPill tone="rejected">
                        Rejected
                      </InlineControlPill>
                    ) : null}

                    {isSuperseded ? (
                      <InlineControlPill tone="historical">
                        Historical
                      </InlineControlPill>
                    ) : null}

                    <SourceTypeBadge sourceType={sourceType} />

                    {fileType ? (
                      <InlineControlPill tone="neutral">{fileType}</InlineControlPill>
                    ) : null}

                    {version.release_status ? (
                      <InlineControlPill tone={getReleaseStatusTone(version.release_status)}>
                        {getReleaseStatusLabel(version.release_status)}
                      </InlineControlPill>
                    ) : null}

                    {version.is_attestable ? (
                      <InlineControlPill tone="active">Attestable</InlineControlPill>
                    ) : null}

                    {previewStatus === "processing" ? (
                      <InlineControlPill tone="neutral">
                        Preview Processing
                      </InlineControlPill>
                    ) : null}
                  </div>
                  <StatusBadge status={version.status} isOutdated={false} />
                </div>

                <div
                  style={{
                    color: "var(--color-text-secondary)",
                    lineHeight: 1.65,
                    fontSize: "0.9rem",
                  }}
                >
                  {getVersionLifecycleText(version.status, isCurrent)}
                </div>

                <div
                  style={{
                    fontSize: "0.9rem",
                    color: "var(--color-text-secondary)",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.75rem",
                  }}
                >
                  <span>Created {formatDateTime(version.created_at)}</span>
                  {version.effective_date ? (
                    <span>Effective {formatDate(version.effective_date)}</span>
                  ) : null}
                  {version.released_at ? (
                    <span>Released {formatDateTime(version.released_at)}</span>
                  ) : null}
                  {version.is_attestable ? (
                    <span>Attestation available</span>
                  ) : null}
                  {getApprovedByLabel(version) ? (
                    <span>Approved by {getApprovedByLabel(version)}</span>
                  ) : null}
                </div>

                {version.change_summary ? (
                  <div style={{ color: "var(--color-text)", lineHeight: 1.6 }}>
                    <strong>Summary:</strong> {version.change_summary}
                  </div>
                ) : null}

                {version.notes ? (
                  <div
                    style={{
                      color: "var(--color-text-secondary)",
                      lineHeight: 1.7,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {version.notes}
                  </div>
                ) : null}

                {isRejected && version.rejection_reason ? (
                  <div
                    style={{
                      border: "1px solid rgba(239, 68, 68, 0.16)",
                      borderRadius: "14px",
                      padding: "0.75rem 0.85rem",
                      background: "rgba(239, 68, 68, 0.035)",
                      color: "#991b1b",
                      lineHeight: 1.6,
                      fontSize: "0.9rem",
                    }}
                  >
                    <strong>Rejection reason:</strong> {version.rejection_reason}
                  </div>
                ) : null}

                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <DownloadAction
                    url={version.pdf_url || version.primary_document_url || ""}
                    label={isCurrent ? "Current PDF" : "PDF"}
                    icon="📄"
                  />
                  <DownloadAction
                    url={version.docx_url || version?.metadata?.urls?.docx || ""}
                    label={isCurrent ? "Current Word" : "Word"}
                    icon="📝"
                  />
                  <DownloadAction
                    url={version.pdf_preview_url || version?.metadata?.urls?.pdf_preview || ""}
                    label={isCurrent ? "Current Preview" : "Preview"}
                    icon="👁️"
                  />

                  {isPending ? (
                    <>
                      {selectedCanApproveVersion ? (
                        <button
                          type="button"
                          className="library-link library-link--compact"
                          onClick={() => openApproveDialog(version)}
                          disabled={approveBusy || rejectBusy}
                        >
                          {approveBusy ? "Approving…" : "Approve"}
                        </button>
                      ) : null}
                      {selectedCanRejectVersion ? (
                        <button
                          type="button"
                          className="library-link--danger library-link--compact"
                          onClick={() => openRejectDialog(version)}
                          disabled={approveBusy || rejectBusy}
                        >
                          {rejectBusy ? "Rejecting…" : "Reject"}
                        </button>
                      ) : null}
                      {!selectedCanApproveVersion && !selectedCanRejectVersion ? (
                        <InlineControlPill tone="historical">
                          Review locked
                        </InlineControlPill>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>
            );
          })
        ) : (
          <div
            style={{
              color: "var(--color-text-secondary)",
              lineHeight: 1.7,
            }}
          >
            No version history is available yet. New saves and future version activity
            will appear here.
          </div>
        )}
      </div>
    );
  };

  const renderAuditTimelineCard = () => {
    return renderFieldCard(
      "Document-control timeline",
      <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
        {auditLoading ? (
          <div style={{ color: "var(--color-text-secondary)", lineHeight: 1.7 }}>
            Loading document-control timeline…
          </div>
        ) : null}

        {auditError ? (
          <div
            style={{
              border: "1px solid rgba(239, 68, 68, 0.18)",
              borderRadius: "14px",
              padding: "0.8rem 0.9rem",
              background: "rgba(239, 68, 68, 0.04)",
              color: "#b91c1c",
              lineHeight: 1.6,
              fontSize: "0.92rem",
            }}
          >
            {auditError}
          </div>
        ) : null}

        {!auditLoading && !auditError && !auditEvents.length ? (
          <div style={{ color: "var(--color-text-secondary)", lineHeight: 1.7 }}>
            No document-control events recorded yet. Future uploads, approvals,
            rejections, superseded versions, and archive actions will appear here.
          </div>
        ) : null}

        {auditEvents.map((event) => {
          const eventTone = getEventTone(event.event_type);
          const versionNumber = getVersionNumberFromEvent(event);
          const statusTransition = [event.from_status, event.to_status]
            .filter(Boolean)
            .join(" → ");

          return (
            <div
              key={event.id || `${event.event_type}-${event.created_at}`}
              style={{
                border:
                  eventTone === "active"
                    ? "1px solid rgba(12, 143, 93, 0.18)"
                    : eventTone === "rejected"
                    ? "1px solid rgba(239, 68, 68, 0.18)"
                    : eventTone === "pending"
                    ? "1px solid rgba(180, 83, 9, 0.18)"
                    : "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 92%)",
                borderRadius: "16px",
                padding: "0.85rem 0.95rem",
                background:
                  eventTone === "active"
                    ? "rgba(12, 143, 93, 0.035)"
                    : eventTone === "rejected"
                    ? "rgba(239, 68, 68, 0.025)"
                    : eventTone === "pending"
                    ? "rgba(180, 83, 9, 0.03)"
                    : "color-mix(in srgb, var(--color-text, #0f172a), transparent 98%)",
                display: "flex",
                flexDirection: "column",
                gap: "0.45rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "0.75rem",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                  <strong style={{ color: "var(--color-text)" }}>{getEventLabel(event.event_type)}</strong>
                  {versionNumber ? <InlineControlPill tone="neutral">v{versionNumber}</InlineControlPill> : null}
                  {statusTransition ? <InlineControlPill tone={eventTone}>{statusTransition}</InlineControlPill> : null}
                </div>
                <span style={{ color: "var(--color-text-secondary)", fontSize: "0.86rem" }}>
                  {formatDateTime(event.created_at)}
                </span>
              </div>

              <div style={{ color: "var(--color-text-secondary)", lineHeight: 1.6, fontSize: "0.9rem" }}>
                Actor: {actorLabel(event.actor)}
              </div>

              {event.reason ? (
                <div style={{ color: "var(--color-text)", lineHeight: 1.6, fontSize: "0.9rem" }}>
                  <strong>Reason:</strong> {event.reason}
                </div>
              ) : null}

              {event.notes ? (
                <div style={{ color: "var(--color-text-secondary)", lineHeight: 1.6, fontSize: "0.9rem", whiteSpace: "pre-wrap" }}>
                  {event.notes}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPreviewPanel = () => {
    if (!detailView?.pdfPreviewUrl) {
      return (
        <div
          style={{
            border:
              "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 90%)",
            borderRadius: "20px",
            padding: "1.25rem",
            color: "var(--color-text-secondary)",
            lineHeight: 1.7,
          }}
        >
          No PDF preview is available for the current controlled version yet. Use
          the download actions above to open the approved document when it becomes
          available.
        </div>
      );
    }

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.85rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              fontSize: "0.94rem",
              color: "var(--color-text-secondary)",
              lineHeight: 1.7,
            }}
          >
            Preview is read-only and follows the current controlled version. Use
            the <strong>Version History</strong> section to inspect pending or
            superseded revisions.
          </div>

          {selectedCanEdit ? (
            <button
              type="button"
              className="library-link library-link--compact"
              onClick={() => setDetailTab("details")}
            >
              Edit in Details
            </button>
          ) : null}
        </div>

        <div
          style={{
            border:
              "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 90%)",
            borderRadius: "20px",
            overflow: "hidden",
            background: "#fff",
            minHeight: "68vh",
          }}
        >
          <iframe
            key={previewKey}
            title="SOP PDF preview"
            src={detailView.pdfPreviewUrl}
            style={{
              width: "100%",
              height: "68vh",
              border: "none",
              display: "block",
            }}
          />
        </div>
      </div>
    );
  };

  const renderDetailModal = () => {
    if (!selectedRowId) return null;

    const detailSaveBusy = Boolean(actionState[`${selectedRowId}:detail-save`]);
    const selectedSubmitBusy = Boolean(actionState[`${selectedRow?.id}:submit`]);
    const selectedArchiveBusy = Boolean(
      actionState[`${selectedRow?.id}:archive`]
    );
    const previewAvailable = Boolean(detailView?.pdfPreviewUrl);
    const currentVersionNumber =
      selectedDetail?.current_version_number ||
      detailView?.latestVersion?.version_number ||
      selectedRow?.latestVersionNumber ||
      null;

    return (
      <div
        onClick={() => closeDetailModal(false)}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.28)",
          backdropFilter: "blur(2px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          zIndex: sopExplainOpen ? 1 : 1000,
        }}
      >
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            width: "min(1100px, 100%)",
            maxHeight: "88vh",
            overflowY: "auto",
            background: "var(--color-surface, #ffffff)",
            borderRadius: "28px",
            border:
              "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 90%)",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
            padding: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "1rem",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.2rem",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "var(--color-text-secondary)",
                }}
              >
                SOP detail
              </div>

              {detailLoading ? (
                <h3
                  style={{
                    margin: 0,
                    fontSize: "1.2rem",
                    lineHeight: 1.25,
                    color: "var(--color-text)",
                  }}
                >
                  Loading SOP detail…
                </h3>
              ) : selectedCanEdit ? (
                <>
                  <input
                    type="text"
                    value={detailForm.title}
                    onChange={(e) =>
                      handleDetailFieldChange("title", e.target.value)
                    }
                    style={{
                      ...modalInputStyle,
                      minHeight: "56px",
                      fontSize: "2rem",
                      fontWeight: 800,
                      lineHeight: 1.05,
                      padding: "0.55rem 0.8rem",
                      border: "none",
                      boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.08)",
                    }}
                  />
                  {(selectedDetail?.code || selectedRow?.code) && (
                    <div
                      style={{
                        color: "var(--color-text-secondary)",
                        fontSize: "1rem",
                      }}
                    >
                      {selectedDetail?.code || selectedRow?.code}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "2rem",
                      lineHeight: 1.05,
                      color: "var(--color-text)",
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedDetail?.title || selectedRow?.title || "Untitled SOP"}
                  </h3>
                  {(selectedDetail?.code || selectedRow?.code) && (
                    <div
                      style={{
                        color: "var(--color-text-secondary)",
                        fontSize: "1rem",
                      }}
                    >
                      {selectedDetail?.code || selectedRow?.code}
                    </div>
                  )}
                </>
              )}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                flex: "0 0 auto",
              }}
            >
              {!detailLoading && !detailError && selectedDetail ? (
                <Button
                  variant="ghost"
                  size="small"
                  onClick={openSopExplain}
                >
                  Explain
                </Button>
              ) : null}

              <button
                type="button"
                onClick={() => closeDetailModal(false)}
                aria-label="Close SOP detail"
                title="Close"
                style={{
                  border: "none",
                  background: "transparent",
                  color: "var(--color-text-secondary)",
                  cursor: "pointer",
                  fontSize: "1.4rem",
                  lineHeight: 1,
                  padding: 0,
                  flex: "0 0 auto",
                }}
              >
                ×
              </button>
            </div>
          </div>

          {detailError ? (
            <div
              style={{
                border: "1px solid rgba(239, 68, 68, 0.18)",
                borderRadius: "18px",
                padding: "1rem",
                background: "rgba(239, 68, 68, 0.04)",
                color: "#b91c1c",
                lineHeight: 1.7,
              }}
            >
              {detailError}
            </div>
          ) : null}

          {saveNotice ? (
            <div
              style={{
                border: "1px solid rgba(12, 143, 93, 0.16)",
                borderRadius: "18px",
                padding: "0.85rem 1rem",
                background: "rgba(12, 143, 93, 0.06)",
                color: "#0C8F5D",
                fontWeight: 600,
              }}
            >
              {saveNotice}
            </div>
          ) : null}

          {!detailLoading && !detailError ? (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: "0.75rem",
                }}
              >
                <div
                  style={{
                    border:
                      "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 90%)",
                    borderRadius: "18px",
                    padding: "1rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--color-text-secondary)",
                      marginBottom: "0.4rem",
                    }}
                  >
                    Status
                  </div>
                  <StatusBadge
                    status={selectedRow?.status || selectedDetail?.status}
                    isOutdated={
                      selectedRow?.isOutdated || selectedDetail?.is_outdated
                    }
                  />
                </div>

                <div
                  style={{
                    border:
                      "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 90%)",
                    borderRadius: "18px",
                    padding: "1rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--color-text-secondary)",
                      marginBottom: "0.4rem",
                    }}
                  >
                    Version
                  </div>
                  <div style={{ fontWeight: 700, color: "var(--color-text)" }}>
                    {currentVersionNumber ? `v${currentVersionNumber}` : "--"}
                  </div>
                </div>

                <div
                  style={{
                    border:
                      "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 90%)",
                    borderRadius: "18px",
                    padding: "1rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--color-text-secondary)",
                      marginBottom: "0.4rem",
                    }}
                  >
                    Release
                  </div>
                  <InlineControlPill tone={getReleaseStatusTone(currentReleaseStatus)}>
                    {getReleaseStatusLabel(currentReleaseStatus)}
                  </InlineControlPill>
                </div>

                <div
                  style={{
                    border:
                      "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 90%)",
                    borderRadius: "18px",
                    padding: "1rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--color-text-secondary)",
                      marginBottom: "0.4rem",
                    }}
                  >
                    Created
                  </div>
                  <div style={{ color: "var(--color-text)" }}>
                    {formatDate(
                      selectedRow?.createdAt ||
                        selectedDetail?.created_at ||
                        null
                    )}
                  </div>
                </div>

                <div
                  style={{
                    border:
                      "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 90%)",
                    borderRadius: "18px",
                    padding: "1rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--color-text-secondary)",
                      marginBottom: "0.4rem",
                    }}
                  >
                    Type
                  </div>
                  <TypeBadge
                    type={
                      selectedRow?.type ||
                      selectedDetail?.type ||
                      selectedRow?.raw?.type ||
                      "guided"
                  }
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "1rem",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    gap: "0.5rem",
                    padding: "0.35rem",
                    borderRadius: "999px",
                    background:
                      "color-mix(in srgb, var(--color-text, #0f172a), transparent 96%)",
                    border:
                      "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 92%)",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setDetailTab("details")}
                    style={{
                      border: "none",
                      borderRadius: "999px",
                      padding: "0.6rem 1rem",
                      cursor: "pointer",
                      fontWeight: 700,
                      background:
                        detailTab === "details"
                          ? "var(--color-surface, #ffffff)"
                          : "transparent",
                      color:
                        detailTab === "details"
                          ? "var(--color-text)"
                          : "var(--color-text-secondary)",
                      boxShadow:
                        detailTab === "details"
                          ? "0 1px 2px rgba(15, 23, 42, 0.08)"
                          : "none",
                    }}
                  >
                    Details
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setDetailTab("preview");
                      setPreviewKey((value) => value + 1);
                    }}
                    style={{
                      border: "none",
                      borderRadius: "999px",
                      padding: "0.6rem 1rem",
                      cursor: previewAvailable ? "pointer" : "not-allowed",
                      fontWeight: 700,
                      background:
                        detailTab === "preview"
                          ? "var(--color-surface, #ffffff)"
                          : "transparent",
                      color:
                        detailTab === "preview"
                          ? "var(--color-text)"
                          : previewAvailable
                          ? "var(--color-text-secondary)"
                          : "color-mix(in srgb, var(--color-text-secondary), transparent 25%)",
                      boxShadow:
                        detailTab === "preview"
                          ? "0 1px 2px rgba(15, 23, 42, 0.08)"
                          : "none",
                    }}
                    disabled={!previewAvailable}
                  >
                    Preview
                  </button>
                </div>

                {renderSaveStateChip()}
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border:
                    "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 92%)",
                  borderRadius: "18px",
                  padding: "0.85rem 0.9rem",
                  background:
                    "color-mix(in srgb, var(--color-text, #0f172a), transparent 98%)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    alignItems: "center",
                  }}
                >
                  {selectedCanEdit ? (
                    <>
                      <button
                        type="button"
                        className="library-link library-link--compact"
                        onClick={saveDetailChanges}
                        disabled={!isDetailDirty || detailSaveBusy}
                      >
                        {detailSaveBusy ? "Saving…" : "Save changes"}
                      </button>
                      <button
                        type="button"
                        className="library-link library-link--compact"
                        onClick={resetDetailForm}
                        disabled={!isDetailDirty || detailSaveBusy}
                      >
                        Reset
                      </button>
                    </>
                  ) : null}

                  <DownloadAction
                    url={detailView?.pdfUrl}
                    label="Download Current PDF"
                    icon="📄"
                  />
                  <DownloadAction
                    url={detailView?.docxUrl}
                    label="Download Current Word"
                    icon="📝"
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  {selectedCanSubmit ? (
                    <button
                      type="button"
                      className="library-link library-link--compact"
                      onClick={() =>
                        selectedRow && performLifecycleAction(selectedRow, "submit")
                      }
                      disabled={
                        selectedSubmitBusy || detailSaveBusy || isDetailDirty
                      }
                      title={
                        isDetailDirty
                          ? "Save changes before submitting for approval."
                          : "Submit draft for approval."
                      }
                    >
                      {selectedSubmitBusy ? "…" : "Submit"}
                    </button>
                  ) : null}

                  {selectedCanArchive ? (
                    <button
                      type="button"
                      className="library-link--danger library-link--compact"
                      onClick={() =>
                        selectedRow && performLifecycleAction(selectedRow, "archive")
                      }
                      disabled={selectedArchiveBusy || detailSaveBusy}
                    >
                      {selectedArchiveBusy ? "…" : "Archive"}
                    </button>
                  ) : null}
                </div>
              </div>

              {detailTab === "details" ? (
                <div
                  style={{
                    fontSize: "0.94rem",
                    color: "var(--color-text-secondary)",
                    lineHeight: 1.7,
                  }}
                >
                  {selectedCanEdit
                    ? "Edit SOP display metadata here. Saves are audit logged and do not change the controlled PDF/Word artifact."
                    : "Review SOP metadata here. Archived and historical records are read-only. Use Preview to inspect the current controlled PDF."}
                </div>
              ) : null}

              {detailTab === "preview" ? (
                renderPreviewPanel()
              ) : (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "0.85rem",
                    }}
                  >
                    {renderFieldCard(
                      "Objective",
                      selectedCanEdit ? (
                        <textarea
                          value={detailForm.objective}
                          onChange={(e) =>
                            handleDetailFieldChange("objective", e.target.value)
                          }
                          style={modalTextareaStyle}
                        />
                      ) : (
                        <div
                          style={{
                            color: "var(--color-text)",
                            lineHeight: 1.7,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {detailView?.objective || "No objective captured yet."}
                        </div>
                      )
                    )}

                    {renderFieldCard(
                      "Process name",
                      selectedCanEdit ? (
                        <input
                          type="text"
                          value={detailForm.processName}
                          onChange={(e) =>
                            handleDetailFieldChange("processName", e.target.value)
                          }
                          style={modalInputStyle}
                        />
                      ) : (
                        <div
                          style={{
                            color: "var(--color-text)",
                            lineHeight: 1.7,
                          }}
                        >
                          {detailView?.processName || "No process name captured yet."}
                        </div>
                      )
                    )}

                    {renderFieldCard(
                      "Compliance requirements",
                      selectedCanEdit ? (
                        <textarea
                          value={detailForm.complianceRequirementsText}
                          onChange={(e) =>
                            handleDetailFieldChange(
                              "complianceRequirementsText",
                              e.target.value
                            )
                          }
                          style={modalTextareaStyle}
                          placeholder="One requirement per line"
                        />
                      ) : detailView?.complianceRequirements?.length ? (
                        <ul
                          style={{
                            margin: 0,
                            paddingLeft: "1.1rem",
                            color: "var(--color-text)",
                            lineHeight: 1.7,
                          }}
                        >
                          {detailView.complianceRequirements.map((item, index) => (
                            <li key={`${item}-${index}`}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <div
                          style={{
                            color: "var(--color-text-secondary)",
                            lineHeight: 1.7,
                          }}
                        >
                          No compliance requirements captured yet.
                        </div>
                      )
                    )}

                    {renderFieldCard(
                      "Current version notes",
                      selectedCanEdit ? (
                        <textarea
                          value={detailForm.latestVersionNotes}
                          onChange={(e) =>
                            handleDetailFieldChange(
                              "latestVersionNotes",
                              e.target.value
                            )
                          }
                          style={modalTextareaStyle}
                        />
                      ) : (
                        <div
                          style={{
                            color: "var(--color-text)",
                            lineHeight: 1.7,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {detailView?.latestVersionNotes ||
                            "No version notes captured yet."}
                        </div>
                      )
                    )}
                  </div>

                  {renderLinkedControlsCard()}
                  {renderReleaseAndAttestationCard()}
                  {renderVersionHistoryCard()}
                  {renderAuditTimelineCard()}
                </>
              )}
            </>
          ) : null}
        </div>
      </div>
    );
  };

  const renderReviewDialog = () => {
    if (!reviewDialog.mode || !reviewDialog.version) return null;

    const isApprove = reviewDialog.mode === "approve";
    const versionNumber = reviewDialog.version?.version_number || "--";
    const actionKey = `${selectedRowId}:review:${reviewDialog.version.id}:${reviewDialog.mode}`;
    const busy = Boolean(actionState[actionKey]);

    return (
      <div
        onClick={closeReviewDialog}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.32)",
          backdropFilter: "blur(2px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          zIndex: 1200,
        }}
      >
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            width: "min(560px, 100%)",
            background: "var(--color-surface, #ffffff)",
            borderRadius: "24px",
            border:
              "1px solid color-mix(in srgb, var(--color-text, #0f172a), transparent 88%)",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.2)",
            padding: "1.25rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.95rem",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
            <div>
              <div
                style={{
                  fontSize: "0.76rem",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "var(--color-text-secondary)",
                  marginBottom: "0.35rem",
                }}
              >
                Reviewer workflow
              </div>
              <h3 style={{ margin: 0, color: "var(--color-text)", fontSize: "1.35rem" }}>
                {isApprove ? "Approve" : "Reject"} version v{versionNumber}
              </h3>
            </div>
            <button
              type="button"
              onClick={closeReviewDialog}
              aria-label="Close reviewer dialog"
              style={{
                border: "none",
                background: "transparent",
                color: "var(--color-text-secondary)",
                cursor: "pointer",
                fontSize: "1.35rem",
                lineHeight: 1,
                padding: 0,
              }}
            >
              ×
            </button>
          </div>

          <div style={{ color: "var(--color-text-secondary)", lineHeight: 1.7 }}>
            {isApprove
              ? "Approval promotes this pending revision to the current controlled version and supersedes the prior active version."
              : "Rejected versions remain visible in history and do not replace the current controlled version."}
          </div>

          {!isApprove ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
              <label style={{ fontSize: "0.82rem", fontWeight: 800, color: "var(--color-text-secondary)" }}>
                Rejection reason <span style={{ color: "#b91c1c" }}>*</span>
              </label>
              <input
                type="text"
                value={reviewDialog.reason}
                onChange={(event) => updateReviewDialogField("reason", event.target.value)}
                placeholder="Explain why this revision is rejected"
                disabled={busy}
                style={modalInputStyle}
              />
            </div>
          ) : null}

          <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
            <label style={{ fontSize: "0.82rem", fontWeight: 800, color: "var(--color-text-secondary)" }}>
              {isApprove ? "Approval summary" : "Reviewer notes"}
            </label>
            <textarea
              value={reviewDialog.notes}
              onChange={(event) => updateReviewDialogField("notes", event.target.value)}
              placeholder={isApprove ? "Optional approval summary" : "Optional remediation notes"}
              disabled={busy}
              style={{ ...modalTextareaStyle, minHeight: "110px" }}
            />
          </div>

          {reviewDialog.error ? (
            <div
              style={{
                border: "1px solid rgba(239, 68, 68, 0.18)",
                borderRadius: "14px",
                padding: "0.8rem 0.9rem",
                background: "rgba(239, 68, 68, 0.04)",
                color: "#b91c1c",
                lineHeight: 1.6,
                fontSize: "0.92rem",
              }}
            >
              {reviewDialog.error}
            </div>
          ) : null}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.6rem", flexWrap: "wrap" }}>
            <button
              type="button"
              className="library-link library-link--compact"
              onClick={closeReviewDialog}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className={isApprove ? "library-link library-link--compact" : "library-link--danger library-link--compact"}
              onClick={submitReviewDecision}
              disabled={busy}
            >
              {busy ? (isApprove ? "Approving…" : "Rejecting…") : isApprove ? "Approve version" : "Reject version"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const resolvedTheme = themeMode === "light" ? "light" : "dark";

  const libraryContent = (
    <>
      {renderReviewDialog()}

      <section className="library-controls" aria-label="Library filters">
        <div className="library-control library-control--search">
          <label htmlFor="library-search">Search</label>
          <div className="library-input-wrapper">
            <span className="library-input-icon" aria-hidden="true">
              🔍
            </span>
            <input
              id="library-search"
              type="text"
              placeholder="Search by title or code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="library-control">
          <label htmlFor="library-category">Category</label>
          <select
            id="library-category"
            value={categoryFilter}
            onChange={(e) => {
              const value = e.target.value;
              setCategoryFilter(value);
              if (value !== "sop") {
                setSopView("docs");
              }
              setTemplateArea("all");
            }}
          >
            <option value="all">All items</option>
            <option value="sop">SOPs</option>
            <option value="compliance">Compliance</option>
            <option value="checklist">Checklists (coming soon)</option>
            <option value="policy">Policies (coming soon)</option>
          </select>
        </div>

        {categoryFilter === "sop" && (
          <div className="library-control">
            <label htmlFor="sop-view">SOP view</label>
            <select
              id="sop-view"
              value={sopView}
              onChange={(e) => {
                setSopView(e.target.value);
                setTemplateArea("all");
              }}
            >
              <option value="docs">My SOPs</option>
              <option value="templates">Templates</option>
            </select>
          </div>
        )}

        {isTemplateView && (
          <div className="library-control">
            <label htmlFor="template-area">Template area</label>
            <select
              id="template-area"
              value={templateArea}
              onChange={(e) => setTemplateArea(e.target.value)}
            >
              <option value="all">All areas</option>
              {templateAreas.map((area) => (
                <option key={area} value={area}>
                  {TEMPLATE_AREA_LABELS[area] || area}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="library-control">
          <label htmlFor="library-sort">Sort</label>
          <select
            id="library-sort"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="az">Title A–Z</option>
          </select>
        </div>

        <div className="library-control">
          <label>&nbsp;</label>
          <Button
            variant="secondary"
            size="medium"
            onClick={handleManualRefresh}
            disabled={loading}
            loading={loading}
            loadingLabel="Refreshing…"
            className="library-refresh-button"
            title="Refresh the SOP library"
          >
            Refresh
          </Button>
        </div>
      </section>

      <section className="library-table-wrapper">
        {isTemplateView ? (
          filteredTemplates.length === 0 ? (
            <div className="library-empty">
              <p>
                No templates match these filters. Try a different area or search
                term.
              </p>
            </div>
          ) : (
            <div className="library-template-grid">
              {filteredTemplates.map((tpl) => (
                <article
                  key={tpl.id}
                  className="library-template-card"
                  aria-label={tpl.title}
                >
                  <header className="library-template-header">
                    <h3>{tpl.title}</h3>
                    <span className="library-template-level">{tpl.level}</span>
                  </header>
                  <p className="library-template-description">
                    {tpl.description}
                  </p>
                  <div className="library-template-meta">
                    <span className="library-template-industry">
                      {tpl.industry}
                    </span>
                  </div>
                  <div className="library-template-tags">
                    {tpl.tags.map((tag) => (
                      <span key={tag} className="library-template-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="library-template-actions">
                    <button
                      type="button"
                      className="library-template-cta"
                      onClick={() => {
                        try {
                          window.localStorage.setItem(
                            "oasys_sop_template_id",
                            tpl.id
                          );
                        } catch (e) {
                          console.error("Unable to store template selection", e);
                        }
                        window.location.hash = "#sop";
                      }}
                    >
                      Use template
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )
        ) : loading ? (
          <div className="library-empty">
            <p>Loading SOP library…</p>
          </div>
        ) : loadError ? (
          <div className="library-empty">
            <p>{loadError}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="library-empty">
            <p>{emptyMessage}</p>
          </div>
        ) : (
          renderTable()
        )}
      </section>

      {renderDetailModal()}

      <ContextualExplainDrawer
        open={sopExplainOpen}
        record={selectedDetail || selectedRow}
        recordLabel={
          selectedDetail?.title ||
          selectedRow?.title ||
          "Selected SOP"
        }
        question={sopExplainQuestion}
        card={sopExplainCard}
        metadata={sopExplainCard?.metadata || null}
        loading={sopExplainLoading}
        error={sopExplainError}
        themeMode={resolvedTheme}
        prompts={SOP_EXPLAIN_PROMPTS}
        contextDescription="Grounded reasoning for this selected controlled procedure. Canonical lifecycle state, current-version authority, effective release state, document-control history, explicit control links, and acknowledgment records remain authoritative."
        onAsk={askSopExplain}
        onClose={closeSopExplain}
      />
    </>
  );

  if (!standalone) {
    return <div className="library">{libraryContent}</div>;
  }

  return (
    <div className={`library library--standalone oasys-theme-${resolvedTheme}`}>
      <div className="library-page-shell">
        <header className="library-page-header">
          <div className="library-page-heading">
            <h1>Library</h1>
            <p>
              Review controlled SOPs, manage document lifecycle state, and open the
              authoritative artifacts your teams use to operate compliantly.
            </p>
          </div>

          {typeof onModeChange === "function" ? (
            <div className="library-page-actions">
              <Button
                variant="secondary"
                size="medium"
                className="library-page-action"
                onClick={() => onModeChange("dashboard")}
              >
                Mission Control
              </Button>
              <Button
                variant="secondary"
                size="medium"
                className="library-page-action"
                onClick={() => onModeChange("sop")}
              >
                Open SOP Center
              </Button>
            </div>
          ) : null}
        </header>

        <section className="library-page-panel">{libraryContent}</section>
      </div>
    </div>
  );
}
